import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { HardHat, SignOut, ChartBar, ListChecks, Users, Package, Wrench, CheckCircle, XCircle, Warning, PaperPlaneTilt, Plus, Trash, Folders, Pulse, ClipboardText, PencilSimple, UserPlus, Gear, CaretUp, CaretDown, CircleNotch, Newspaper, MapPin, X, MagnifyingGlass } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import api, {
  uploadAdminProductImage,
  publicAssetUrl,
  PRODUCT_IMAGE_MAX_KB,
  PRODUCT_IMAGE_MAX_BYTES,
} from "@/lib/api";
import { parseProductsSupplied, filterProductsSuppliedToCatalog } from "@/lib/supplierProducts";
import { parseProductSizes, formatProductSizes } from "@/lib/productSizes";
import CityGoogleAutocomplete from "@/components/CityGoogleAutocomplete";
import AdminOldMaterialTab from "@/components/AdminOldMaterialTab";

const ADD_LEAD_CITY_CLASS = "add-lead-city-autocomplete mt-1 w-full";
const SUPPLIER_EDIT_CITY_CLASS = "supplier-edit-city-autocomplete mt-1 w-full max-w-xs";

/** Radix Select sentinel — no parent row (e.g. promote subcategory to top-level) */
const CATEGORY_PARENT_NONE = "__none__";
/** create-sub: placeholder until user picks a top-level parent */
const SUB_PARENT_UNSET = "__sub_parent_unset__";

function normCategoryParentId(v) {
  return v && String(v).trim() ? String(v).trim() : "";
}

function sortCategoriesByOrderThenName(arr) {
  return [...(arr || [])].sort((a, b) => {
    const ao = Number.isFinite(Number(a.sort_order)) ? Number(a.sort_order) : 9999;
    const bo = Number.isFinite(Number(b.sort_order)) ? Number(b.sort_order) : 9999;
    if (ao !== bo) return ao - bo;
    return String(a.name || "").localeCompare(String(b.name || ""));
  });
}

/** Normalize free text for supplier name/city search (collapse spaces, strip diacritics). */
function normalizeAssignSearchText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function supplierCitiesSearchText(supplier) {
  const cities = supplier?.cities_served;
  if (Array.isArray(cities)) return cities.map((c) => String(c || "")).join(" ");
  if (typeof cities === "string") return cities;
  return "";
}

function supplierMatchesAssignSearch(supplier, queryNormalized) {
  if (!queryNormalized) return true;
  const haystack = normalizeAssignSearchText(
    [
      supplier?.name,
      supplier?.company_name,
      supplierCitiesSearchText(supplier),
    ]
      .filter(Boolean)
      .join(" ")
  );
  if (!haystack) return false;
  const tokens = queryNormalized.split(" ").filter(Boolean);
  return tokens.every((token) => haystack.includes(token));
}

const ASSIGN_SUPPLIER_PAGE_SIZE = 10;
const ASSIGN_SUPPLIER_SEARCH_PAGE_SIZE = 25;

function AssignSupplierQtyHint({ supplier, assignDialog, resolveItemProductId }) {
  const lines = parseProductsSupplied(supplier.products_supplied);
  if (!assignDialog?.items?.length || !lines.length) return null;
  const hints = [];
  for (const item of assignDialog.items) {
    const pid = resolveItemProductId(item);
    if (!pid) continue;
    const row = lines.find((l) => l.product_id === pid);
    if (!row) continue;
    const name = item.product_name || "Item";
    const cap = row.quantity;
    if (item.spec_type === "area") {
      hints.push({ key: `${pid}-area`, text: `${name}: area line — unit count N/A`, tone: "neutral" });
      continue;
    }
    const need = item.quantity ?? 0;
    if (cap == null) {
      hints.push({ key: pid, text: `${name}: buyer needs ${need} — capacity not set`, tone: "warn" });
    } else if (cap >= need) {
      hints.push({ key: pid, text: `${name}: needs ${need} — supplier has ${cap}`, tone: "ok" });
    } else {
      hints.push({ key: pid, text: `${name}: needs ${need} — supplier has ${cap} (shortfall)`, tone: "short" });
    }
  }
  if (!hints.length) return null;
  return (
    <ul className="text-[11px] mt-1 space-y-0.5 list-none pl-0">
      {hints.map((h) => (
        <li
          key={h.key}
          className={
            h.tone === "short"
              ? "text-amber-800 font-medium"
              : h.tone === "ok"
                ? "text-green-700"
                : "text-neutral-600"
          }
        >
          {h.text}
        </li>
      ))}
    </ul>
  );
}

const LEAD_STATUSES = { pending: "Pending", verified: "Verified", not_reachable: "Not Reachable", fake: "Fake" };

/** Supplier assignment pipeline stages (aligned with supplier dashboard). */
const ASSIGNMENT_PIPELINE = {
  new: { label: "New Lead", color: "bg-blue-100 text-blue-800 border-blue-300" },
  contacted: { label: "Contacted", color: "bg-amber-100 text-amber-800 border-amber-300" },
  quotation_sent: { label: "Quotation Sent", color: "bg-violet-100 text-violet-800 border-violet-300" },
  won: { label: "Won", color: "bg-green-100 text-green-800 border-green-300" },
  lost: { label: "Lost", color: "bg-red-100 text-red-800 border-red-300" },
  expired: { label: "Expired", color: "bg-neutral-200 text-neutral-700 border-neutral-400" },
};

/** Admin suppliers table: pipeline count columns (keys match API `pipeline_counts`). */
const SUPPLIER_TABLE_PIPELINE_STAGES = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "quotation_sent", label: "Quoted" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
  { key: "expired", label: "Expired" },
];

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  verified: "bg-green-100 text-green-800 border-green-300",
  not_reachable: "bg-orange-100 text-orange-800 border-orange-300",
  fake: "bg-red-100 text-red-800 border-red-300"
};

/** Radix Select sentinel for “no staff assignee” (must not be a valid Mongo id). */
const UNASSIGNED_AGENT = "__unassigned__";

/** Active suppliers only (omit deactivated accounts from pickers/lists). */
function isSupplierActive(s) {
  return s?.is_active !== false;
}

const DURATION_OPTIONS = [
  "1 Month", "2 Months", "3 Months", "4 Months", "5 Months", "6 Months",
  "9 Months", "1 Year", "1.5 Years", "2 Years", "3 Years",
];

const CUSTOM_DURATION = "__custom__";

function newLineKey() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `ln-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function slugifyName(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/(^-|-$)/g, "");
  return s;
}

function emptyLeadEditLine() {
  return {
    key: newLineKey(),
    catalogId: CUSTOM_DURATION,
    product_name: "",
    product_id: null,
    spec_type: "quantity",
    quantityStr: "1",
    area_value: "",
    area_unit: "sqft",
    size: "",
  };
}

function leadToEditForm(lead, productList) {
  const items = lead.items || [];
  const lines = items.map((it, idx) => {
    const fromCat = it.product_id && productList.some((p) => p.id === it.product_id);
    return {
      key: `${lead.id}-${idx}-${newLineKey()}`,
      catalogId: fromCat ? it.product_id : CUSTOM_DURATION,
      product_name: it.product_name || "",
      product_id: it.product_id || null,
      spec_type: it.spec_type === "area" ? "area" : "quantity",
      quantityStr: String(it.quantity ?? 1),
      area_value: it.area_value != null ? String(it.area_value) : "",
      area_unit: it.area_unit === "sqm" ? "sqm" : "sqft",
      size: it.size || "",
    };
  });
  const dur = lead.duration || "";
  const preset = DURATION_OPTIONS.includes(dur);
  return {
    leadId: lead.id,
    city: lead.city || "",
    start_date: lead.start_date || "",
    durationMode: preset ? dur : CUSTOM_DURATION,
    customDuration: preset ? "" : dur,
    lines: lines.length ? lines : [emptyLeadEditLine()],
  };
}

function mapLeadLineCatalogSelection(line, catalogValue, productList) {
  if (catalogValue === CUSTOM_DURATION) {
    return { ...line, catalogId: CUSTOM_DURATION, product_id: null, size: "" };
  }
  const pr = productList.find((x) => x.id === catalogValue);
  const sizeOpts = parseProductSizes(pr?.sizes);
  return {
    ...line,
    catalogId: catalogValue,
    product_id: catalogValue,
    product_name: pr ? pr.name : line.product_name,
    size: sizeOpts.length === 1 ? sizeOpts[0] : "",
  };
}

function buildLeadItemFromLine(ln, lineIndex, productList) {
  const name = (ln.product_name || "").trim();
  if (!name) {
    return { error: `Line ${lineIndex + 1}: enter a product name` };
  }
  const sizeOpts = ln.product_id
    ? parseProductSizes(productList.find((p) => p.id === ln.product_id)?.sizes)
    : [];
  if (sizeOpts.length > 0 && !String(ln.size || "").trim()) {
    return { error: `Line ${lineIndex + 1}: select a size` };
  }
  const sizeVal = String(ln.size || "").trim();
  const withSize = (row) => (sizeVal ? { ...row, size: sizeVal } : row);

  if (ln.spec_type === "area") {
    const av = parseFloat(String(ln.area_value).replace(/,/g, ""));
    if (!Number.isFinite(av) || av <= 0) {
      return { error: `Line ${lineIndex + 1}: enter a valid area greater than 0` };
    }
    return {
      item: withSize({
        product_name: name,
        product_id: ln.product_id || null,
        spec_type: "area",
        area_value: av,
        area_unit: ln.area_unit === "sqm" ? "sqm" : "sqft",
        quantity: null,
      }),
    };
  }

  const n = parseInt(String(ln.quantityStr).replace(/\D/g, ""), 10);
  if (!Number.isFinite(n) || n < 1) {
    return { error: `Line ${lineIndex + 1}: quantity must be at least 1` };
  }
  return {
    item: withSize({
      product_name: name,
      product_id: ln.product_id || null,
      spec_type: "quantity",
      quantity: n,
      area_value: null,
      area_unit: null,
    }),
  };
}

function LeadLineSizeSelect({ line, products, value, onChange, className, testId }) {
  const sizeOpts = line.product_id
    ? parseProductSizes(products.find((p) => p.id === line.product_id)?.sizes)
    : [];
  if (!sizeOpts.length) {
    return <span className="text-xs text-neutral-400 flex items-center min-h-9 px-1">—</span>;
  }
  return (
    <Select
      value={value?.trim() ? value.trim() : "__none__"}
      onValueChange={(v) => onChange(v === "__none__" ? "" : v)}
    >
      <SelectTrigger
        className={className || "rounded-sm border-neutral-300 h-9 text-xs w-full"}
        data-testid={testId}
      >
        <SelectValue placeholder="Select size" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__" className="text-neutral-500">Select size</SelectItem>
        {sizeOpts.map((s) => (
          <SelectItem key={s} value={s}>{s}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ProductSizesEditor({ sizes, onChange, testIdPrefix = "product-sizes" }) {
  const [draft, setDraft] = useState("");

  const addSize = () => {
    const val = draft.trim();
    if (!val) return;
    if (sizes.some((s) => s.toLowerCase() === val.toLowerCase())) {
      toast.error("Size already added");
      return;
    }
    onChange([...sizes, val]);
    setDraft("");
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          data-testid={`${testIdPrefix}-draft`}
          placeholder="e.g. 2m"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addSize();
            }
          }}
          className="rounded-sm border-neutral-300 flex-1"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addSize}
          data-testid={`${testIdPrefix}-add`}
          className="rounded-sm shrink-0 h-9"
        >
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>
      {sizes.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {sizes.map((s) => (
            <Badge
              key={s}
              variant="secondary"
              className="rounded-sm text-xs pl-2 pr-1 py-1 gap-1 flex items-center"
            >
              {s}
              <button
                type="button"
                className="inline-flex items-center justify-center w-5 h-5 rounded-sm hover:bg-neutral-200 text-neutral-600"
                aria-label={`Remove size ${s}`}
                data-testid={`${testIdPrefix}-remove-${s}`}
                onClick={() => onChange(sizes.filter((x) => x !== s))}
              >
                <XCircle size={14} weight="bold" />
              </button>
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-neutral-500">No sizes added yet.</p>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAgent = user?.role === "agent";
  const [tab, setTab] = useState("overview");
  const [metrics, setMetrics] = useState({});
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadPage, setLeadPage] = useState(1);
  const [leadRowsPerPage, setLeadRowsPerPage] = useState(25);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersTotal, setSuppliersTotal] = useState(0);
  const [supplierPage, setSupplierPage] = useState(1);
  const [supplierRowsPerPage, setSupplierRowsPerPage] = useState(25);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [assignPickerSuppliers, setAssignPickerSuppliers] = useState([]);
  const [showInactiveSuppliers, setShowInactiveSuppliers] = useState(false);
  const [supplierSearchInput, setSupplierSearchInput] = useState("");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [categoryDialogMode, setCategoryDialogMode] = useState(null); // "create-parent" | "create-sub" | "edit" | null
  const [parentCategoriesModalOpen, setParentCategoriesModalOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    id: "",
    name: "",
    parent_id: "",
    type: "rent",
    tier: "listed",
    is_active: true,
    sort_order: 0,
    is_parent_group: false,
  });
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryActionLoading, setCategoryActionLoading] = useState(false);
  const [serviceProviders, setServiceProviders] = useState([]);
  const [leadFilter, setLeadFilter] = useState("all");
  const [leadSearchInput, setLeadSearchInput] = useState("");
  const [leadSearch, setLeadSearch] = useState("");
  const [assignDialog, setAssignDialog] = useState(null);
  const [assignSupplierSearch, setAssignSupplierSearch] = useState("");
  const [assignSupplierSearchQ, setAssignSupplierSearchQ] = useState("");
  const [assignSearchItems, setAssignSearchItems] = useState([]);
  const [assignSearchTotal, setAssignSearchTotal] = useState(0);
  const [assignSearchLoading, setAssignSearchLoading] = useState(false);
  const [assignSupplierPage, setAssignSupplierPage] = useState(1);
  const [pipelineStatusDialog, setPipelineStatusDialog] = useState(null);
  const [pipelineStatusLoading, setPipelineStatusLoading] = useState(false);
  const [pipelineStageSavingId, setPipelineStageSavingId] = useState(null);
  const [leadStatusSavingId, setLeadStatusSavingId] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [productDialog, setProductDialog] = useState(false);
  const [productEdit, setProductEdit] = useState(null);
  const [productEditImageFile, setProductEditImageFile] = useState(null);
  const [newProduct, setNewProduct] = useState({ name: "", category: "General", image_url: "", sizeList: [] });
  const [newProductImageFile, setNewProductImageFile] = useState(null);
  const newProductImagePreview = useMemo(
    () => (newProductImageFile ? URL.createObjectURL(newProductImageFile) : ""),
    [newProductImageFile]
  );
  const productEditImagePreview = useMemo(
    () => (productEditImageFile ? URL.createObjectURL(productEditImageFile) : ""),
    [productEditImageFile]
  );
  useEffect(() => {
    return () => {
      if (newProductImagePreview) URL.revokeObjectURL(newProductImagePreview);
    };
  }, [newProductImagePreview]);
  useEffect(() => {
    return () => {
      if (productEditImagePreview) URL.revokeObjectURL(productEditImagePreview);
    };
  }, [productEditImagePreview]);
  const [spDialog, setSpDialog] = useState(false);
  const [newSP, setNewSP] = useState({ name: "", category: "labor", phone: "", city: "", description: "" });
  const [loading, setLoading] = useState(true);
  const [productRequests, setProductRequests] = useState([]);
  const [prFilter, setPrFilter] = useState("pending");
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [supplierEdit, setSupplierEdit] = useState(null);
  const [supplierEditCitySearch, setSupplierEditCitySearch] = useState("");
  const [supplierEditSaving, setSupplierEditSaving] = useState(false);
  const [supplierDeleteTarget, setSupplierDeleteTarget] = useState(null);
  const [supplierDeleteLoading, setSupplierDeleteLoading] = useState(false);
  const [leadDeleteTarget, setLeadDeleteTarget] = useState(null);
  const [leadDeleteLoading, setLeadDeleteLoading] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState(() => new Set());
  const [bulkLeadDeleteOpen, setBulkLeadDeleteOpen] = useState(false);
  const [bulkLeadDeleteLoading, setBulkLeadDeleteLoading] = useState(false);
  const [adminCities, setAdminCities] = useState([]);
  const [leadEdit, setLeadEdit] = useState(null);
  const [leadEditSaving, setLeadEditSaving] = useState(false);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [addLeadForm, setAddLeadForm] = useState(null);
  const [addLeadSaving, setAddLeadSaving] = useState(false);
  const [agents, setAgents] = useState([]);
  const [appSettings, setAppSettings] = useState({
    auto_assign_agents: true,
    whatsapp_notify_suppliers: false,
    whatsapp_notify_staff: false,
  });
  const [staffDialogMode, setStaffDialogMode] = useState(null);
  const [staffForm, setStaffForm] = useState({ email: "", password: "", name: "", phone: "", is_active: true });
  const [staffEditId, setStaffEditId] = useState(null);
  const [staffDeleteTarget, setStaffDeleteTarget] = useState(null);
  const [staffSaving, setStaffSaving] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogDialogOpen, setBlogDialogOpen] = useState(false);
  const [blogEditId, setBlogEditId] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    body: "",
    published: false,
  });
  const [blogSaving, setBlogSaving] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const params = { page: leadPage, limit: leadRowsPerPage };
      if (leadFilter && leadFilter !== "all") params.status = leadFilter;
      if (leadSearch) params.q = leadSearch;
      const r = await api.get("/leads", { params });
      setLeads(r.data?.items || []);
      setLeadsTotal(Number(r.data?.total || 0));
    } catch (err) {
      toast.error("Failed to load leads");
    }
    setLeadsLoading(false);
  }, [leadFilter, leadPage, leadRowsPerPage, leadSearch]);

  const fetchSuppliers = useCallback(async () => {
    if (isAgent) return;
    setSuppliersLoading(true);
    try {
      const params = { page: supplierPage, limit: supplierRowsPerPage };
      if (showInactiveSuppliers) params.include_inactive = true;
      if (supplierSearch) params.q = supplierSearch;
      const r = await api.get("/admin/suppliers", { params });
      setSuppliers(r.data?.items || []);
      setSuppliersTotal(Number(r.data?.total || 0));
    } catch {
      toast.error("Failed to load suppliers");
    }
    setSuppliersLoading(false);
  }, [isAgent, supplierPage, supplierRowsPerPage, showInactiveSuppliers, supplierSearch]);

  const fetchAll = useCallback(async () => {
    try {
      const baseCalls = [
        api.get("/admin/metrics"),
        api.get("/products?limit=100"),
        api.get("/admin/product-categories"),
        api.get("/service-providers?limit=100"),
        api.get("/admin/product-requests?status=all"),
        api.get("/public/cities"),
        api.get("/admin/blog-posts"),
      ];
      const adminOnlyCalls = [
        api.get("/admin/agents"),
        api.get("/admin/settings"),
      ];

      const results = await Promise.all(isAgent ? baseCalls : [...baseCalls, ...adminOnlyCalls]);
      const [m, p, pc, sp, pr, ct, bp, ag, st] = isAgent
        ? [...results, null, null]
        : results;
      setMetrics(m.data);
      setProducts(p.data.items || p.data);
      const cats = pc.data.items || [];
      setProductCategories(cats);
      setServiceProviders(sp.data.items || sp.data);
      setProductRequests(pr.data.items || []);
      setAdminCities(Array.isArray(ct.data) ? ct.data : []);
      setBlogPosts(bp.data?.items || []);
      if (!isAgent) {
        setAgents(ag.data.items || []);
        setAppSettings({
          auto_assign_agents: st.data?.auto_assign_agents !== false,
          whatsapp_notify_suppliers: !!st.data?.whatsapp_notify_suppliers,
          whatsapp_notify_staff: !!st.data?.whatsapp_notify_staff,
        });
      }
    } catch (err) {
      toast.error("Failed to load dashboard data");
    }
    setLoading(false);
  }, [isAgent]);

  const onShowInactiveSuppliersChange = (checked) => {
    setShowInactiveSuppliers(checked);
    setSupplierPage(1);
  };

  useEffect(() => {
    const next = leadSearchInput.trim();
    const t = setTimeout(() => {
      setLeadSearch((prev) => {
        if (prev === next) return prev;
        setLeadPage(1);
        setSelectedLeadIds(new Set());
        return next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [leadSearchInput]);

  useEffect(() => {
    const next = supplierSearchInput.trim();
    const t = setTimeout(() => {
      setSupplierSearch((prev) => {
        if (prev === next) return prev;
        setSupplierPage(1);
        return next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [supplierSearchInput]);

  useEffect(() => {
    const next = assignSupplierSearch.trim();
    const t = setTimeout(() => {
      setAssignSupplierSearchQ((prev) => {
        if (prev === next) return prev;
        setAssignSupplierPage(1);
        return next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [assignSupplierSearch]);

  useEffect(() => {
    if (!assignDialog?.id || !assignSupplierSearchQ) {
      return;
    }
    let cancelled = false;
    setAssignSearchLoading(true);
    (async () => {
      try {
        const r = await api.get("/admin/suppliers", {
          params: {
            page: assignSupplierPage,
            limit: ASSIGN_SUPPLIER_SEARCH_PAGE_SIZE,
            q: assignSupplierSearchQ,
          },
        });
        if (cancelled) return;
        setAssignSearchItems((r.data?.items || []).filter(isSupplierActive));
        setAssignSearchTotal(Number(r.data?.total || 0));
      } catch {
        if (!cancelled) {
          toast.error("Failed to search suppliers");
          setAssignSearchItems([]);
          setAssignSearchTotal(0);
        }
      } finally {
        if (!cancelled) setAssignSearchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignDialog?.id, assignSupplierSearchQ, assignSupplierPage]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  useEffect(() => {
    if (isAgent && (tab === "suppliers" || tab === "settings")) setTab("overview");
  }, [isAgent, tab]);

  const updateLeadStatus = async (leadId, status) => {
    setLeadStatusSavingId(leadId);
    try {
      await api.put(`/leads/${leadId}/verify`, { status });
      setLeads((prev) =>
        prev.map((lead) => (lead.id === leadId ? { ...lead, status } : lead))
      );
      toast.success(`Lead status updated to ${LEAD_STATUSES[status] || status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update lead status");
    } finally {
      setLeadStatusSavingId(null);
    }
  };

  const toggleAutoAssignAgents = async (checked) => {
    try {
      const r = await api.put("/admin/settings", { auto_assign_agents: checked });
      setAppSettings((prev) => ({
        ...prev,
        auto_assign_agents: r.data?.auto_assign_agents !== false,
        whatsapp_notify_suppliers: !!r.data?.whatsapp_notify_suppliers,
        whatsapp_notify_staff: !!r.data?.whatsapp_notify_staff,
      }));
      toast.success(checked ? "New leads will auto-assign to staff in rotation" : "Auto-assignment paused");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update settings");
    }
  };

  const toggleWhatsAppSuppliers = async (checked) => {
    try {
      const r = await api.put("/admin/settings", { whatsapp_notify_suppliers: checked });
      setAppSettings((prev) => ({
        ...prev,
        auto_assign_agents: r.data?.auto_assign_agents !== false,
        whatsapp_notify_suppliers: !!r.data?.whatsapp_notify_suppliers,
        whatsapp_notify_staff: !!r.data?.whatsapp_notify_staff,
      }));
      toast.success(
        checked ? "WhatsApp will notify suppliers when leads are assigned" : "Supplier WhatsApp notifications off",
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update settings");
    }
  };

  const toggleWhatsAppStaff = async (checked) => {
    try {
      const r = await api.put("/admin/settings", { whatsapp_notify_staff: checked });
      setAppSettings((prev) => ({
        ...prev,
        auto_assign_agents: r.data?.auto_assign_agents !== false,
        whatsapp_notify_suppliers: !!r.data?.whatsapp_notify_suppliers,
        whatsapp_notify_staff: !!r.data?.whatsapp_notify_staff,
      }));
      toast.success(
        checked ? "WhatsApp will notify staff when leads are assigned to them" : "Staff WhatsApp notifications off",
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update settings");
    }
  };

  const onReassignLeadAgent = async (leadId, value) => {
    try {
      await api.put(`/admin/leads/${leadId}/assigned-agent`, {
        agent_id: value === UNASSIGNED_AGENT ? null : value,
      });
      toast.success("Staff assignment updated");
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update assignment");
    }
  };

  const openStaffCreate = () => {
    setStaffDialogMode("create");
    setStaffEditId(null);
    setStaffForm({ email: "", password: "", name: "", phone: "", is_active: true });
  };

  const openStaffEdit = (a) => {
    setStaffDialogMode("edit");
    setStaffEditId(a.id);
    setStaffForm({
      email: a.email || "",
      password: "",
      name: a.name || "",
      phone: a.phone || "",
      is_active: a.is_active !== false,
    });
  };

  const saveStaffUser = async () => {
    if (!staffForm.email?.trim()) {
      toast.error("Email is required");
      return;
    }
    if (staffDialogMode === "create" && !staffForm.password) {
      toast.error("Password is required");
      return;
    }
    setStaffSaving(true);
    try {
      if (staffDialogMode === "create") {
        await api.post("/admin/agents", {
          email: staffForm.email.trim(),
          password: staffForm.password,
          name: (staffForm.name || "").trim(),
          phone: (staffForm.phone || "").trim(),
        });
        toast.success("Staff user created");
      } else {
        const body = {
          email: staffForm.email.trim(),
          name: (staffForm.name || "").trim(),
          phone: (staffForm.phone || "").trim(),
          is_active: staffForm.is_active,
        };
        if (staffForm.password) body.password = staffForm.password;
        await api.put(`/admin/agents/${staffEditId}`, body);
        toast.success("Staff user updated");
      }
      setStaffDialogMode(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
    setStaffSaving(false);
  };

  const confirmDeleteStaff = async () => {
    if (!staffDeleteTarget) return;
    try {
      await api.delete(`/admin/agents/${staffDeleteTarget.id}`);
      toast.success("Staff user deactivated; their leads were redistributed");
      setStaffDeleteTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to deactivate user");
    }
  };

  const agentSelectOptions = (lead) => {
    const active = agents.filter((a) => a.is_active !== false);
    const cid = lead.assigned_agent_id;
    if (cid && !active.some((a) => a.id === cid)) {
      const cur = agents.find((a) => a.id === cid);
      return cur ? [cur, ...active.filter((a) => a.id !== cur.id)] : active;
    }
    return active;
  };

  const openAssign = async (lead) => {
    const snapshot = { ...lead, assignments: lead.assignments || [] };
    const initialIds = (snapshot.assignments || []).map((a) => a.supplier_id).filter(Boolean);
    setAssignDialog(snapshot);
    setAssignSupplierSearch("");
    setAssignSupplierSearchQ("");
    setAssignSearchItems([]);
    setAssignSearchTotal(0);
    setAssignSupplierPage(1);
    setSelectedSuppliers(initialIds);
    setAssignPickerSuppliers([]);
    try {
      const r = await api.get(`/leads/${lead.id}/assignments`);
      const list = Array.isArray(r.data) ? r.data : [];
      setAssignDialog((prev) => (prev && prev.id === lead.id ? { ...prev, assignments: list } : prev));
      setSelectedSuppliers(list.map((a) => a.supplier_id).filter(Boolean));
    } catch {
      /* keep snapshot from list */
    }
    try {
      const all = [];
      let page = 1;
      const limit = 100;
      while (page <= 50) {
        const r = await api.get("/admin/suppliers", { params: { page, limit } });
        const items = r.data?.items || [];
        all.push(...items);
        const total = Number(r.data?.total || 0);
        if (all.length >= total || items.length === 0) break;
        page += 1;
      }
      setAssignPickerSuppliers(all);
    } catch {
      /* picker stays empty; retry by reopening */
    }
  };

  const openLeadEdit = (lead) => {
    setLeadEdit(leadToEditForm(lead, products));
  };

  const openAddLead = () => {
    setAddLeadForm({
      buyer_phone: "",
      buyer_name: "",
      buyer_company: "",
      requirement_type: "rent",
      city: "",
      start_date: "",
      durationMode: DURATION_OPTIONS[0],
      customDuration: "",
      lines: [emptyLeadEditLine()],
    });
    setAddLeadOpen(true);
  };

  const saveAddLead = async () => {
    if (!addLeadForm) return;
    const phone = (addLeadForm.buyer_phone || "").trim();
    if (!phone) { toast.error("Enter buyer phone number"); return; }
    if (!addLeadForm.city?.trim()) { toast.error("Enter a city"); return; }
    if (!addLeadForm.start_date?.trim()) { toast.error("Enter a start date"); return; }
    const finalDur =
      addLeadForm.durationMode === CUSTOM_DURATION
        ? addLeadForm.customDuration.trim()
        : addLeadForm.durationMode;
    if (addLeadForm.requirement_type === "rent" && !finalDur) {
      toast.error("Select or enter duration");
      return;
    }
    if (!addLeadForm.lines.length) { toast.error("Add at least one product line"); return; }
    const items = [];
    for (let i = 0; i < addLeadForm.lines.length; i++) {
      const built = buildLeadItemFromLine(addLeadForm.lines[i], i, products);
      if (built.error) {
        toast.error(built.error);
        return;
      }
      items.push(built.item);
    }
    setAddLeadSaving(true);
    try {
      await api.post("/admin/leads", {
        buyer_phone: phone,
        buyer_name: addLeadForm.buyer_name?.trim() || "",
        buyer_company: addLeadForm.buyer_company?.trim() || "",
        requirement_type: addLeadForm.requirement_type || "rent",
        city: addLeadForm.city.trim(),
        start_date: addLeadForm.start_date.trim(),
        duration: addLeadForm.requirement_type === "rent" ? finalDur : "N/A",
        items,
      });
      toast.success("Lead created");
      setAddLeadOpen(false);
      setAddLeadForm(null);
      fetchLeads();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e.msg || e).join(", ") : "Failed to create lead");
    }
    setAddLeadSaving(false);
  };

  const leadHasProducts = (lead) => Array.isArray(lead.items) && lead.items.length > 0;

  const saveLeadEdit = async () => {
    if (!leadEdit) return;
    if (!leadEdit.city?.trim()) {
      toast.error("Enter a city");
      return;
    }
    if (!leadEdit.start_date?.trim()) {
      toast.error("Enter a start date");
      return;
    }
    const finalDur =
      leadEdit.durationMode === CUSTOM_DURATION ? leadEdit.customDuration.trim() : leadEdit.durationMode;
    if (!finalDur) {
      toast.error("Select or enter duration");
      return;
    }
    if (!leadEdit.lines.length) {
      toast.error("Add at least one product line");
      return;
    }
    const items = [];
    for (let i = 0; i < leadEdit.lines.length; i++) {
      const built = buildLeadItemFromLine(leadEdit.lines[i], i, products);
      if (built.error) {
        toast.error(built.error);
        return;
      }
      items.push(built.item);
    }
    setLeadEditSaving(true);
    try {
      await api.put(`/leads/${leadEdit.leadId}`, {
        city: leadEdit.city.trim(),
        start_date: leadEdit.start_date.trim(),
        duration: finalDur,
        items,
      });
      toast.success("Lead updated");
      setLeadEdit(null);
      fetchLeads();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e.msg || e).join(", ") : "Failed to update lead");
    }
    setLeadEditSaving(false);
  };

  const openPipelineStatus = async (lead) => {
    setPipelineStatusDialog({ lead, assignments: [...(lead.assignments || [])] });
    setPipelineStatusLoading(true);
    try {
      const r = await api.get(`/leads/${lead.id}`);
      const raw = r.data && r.data.assignments;
      const list = Array.isArray(raw) ? raw : [];
      setPipelineStatusDialog({ lead, assignments: list });
    } catch (err) {
      toast.error((err.response && err.response.data && err.response.data.detail) || "Could not load pipeline status");
      setPipelineStatusDialog({ lead, assignments: [...(lead.assignments || [])] });
    } finally {
      setPipelineStatusLoading(false);
    }
  };

  const assignmentStageOptions = (assignment) => {
    if (Array.isArray(assignment.pipeline_stage_options) && assignment.pipeline_stage_options.length > 0) {
      const options = [...assignment.pipeline_stage_options];
      if (
        assignment.status &&
        !options.some((stage) => stage.key === assignment.status)
      ) {
        options.push({
          key: assignment.status,
          label:
            ASSIGNMENT_PIPELINE[assignment.status]?.label ||
            String(assignment.status).replace(/^custom_/, "").replace(/_/g, " "),
        });
      }
      return options;
    }
    return Object.entries(ASSIGNMENT_PIPELINE)
      .filter(([key]) => key !== "expired" || assignment.status === "expired")
      .map(([key, meta]) => ({ key, label: meta.label }));
  };

  const updateAssignmentStage = async (assignmentId, newStatus) => {
    setPipelineStageSavingId(assignmentId);
    try {
      await api.put(`/admin/assignments/${assignmentId}/stage`, { status: newStatus });
      setPipelineStatusDialog((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          assignments: prev.assignments.map((a) =>
            a.id === assignmentId ? { ...a, status: newStatus } : a
          ),
        };
      });
      setLeads((prev) =>
        prev.map((lead) => {
          if (!lead.assignments?.some((a) => a.id === assignmentId)) return lead;
          return {
            ...lead,
            assignments: lead.assignments.map((a) =>
              a.id === assignmentId ? { ...a, status: newStatus } : a
            ),
          };
        })
      );
      const label =
        ASSIGNMENT_PIPELINE[newStatus]?.label ||
        String(newStatus).replace(/^custom_/, "").replace(/_/g, " ");
      toast.success(`Stage updated to ${label}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update stage");
    } finally {
      setPipelineStageSavingId(null);
    }
  };

  const assignLead = async () => {
    if (selectedSuppliers.length < minRequired || selectedSuppliers.length > 7) {
      toast.error(`Select ${minRequired}-7 suppliers`);
      return;
    }
    try {
      const leadId = assignDialog.id;
      const r = await api.post(`/leads/${leadId}/assign`, { supplier_ids: selectedSuppliers });
      const assignments = Array.isArray(r.data?.assignments) ? r.data.assignments : [];
      setLeads((prev) =>
        prev.map((lead) => (lead.id === leadId ? { ...lead, assignments } : lead))
      );
      toast.success("Lead assigned to suppliers");
      setAssignDialog(null);
      setAssignSupplierSearch("");
      setAssignSupplierPage(1);
      setSelectedSuppliers([]);
      fetchLeads();
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to assign"); }
  };

  const toggleSupplier = (id) => {
    setSelectedSuppliers(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const openSupplierEdit = (s) => {
    const parsed = filterProductsSuppliedToCatalog(
      parseProductsSupplied(s.products_supplied),
      products
    );
    setSupplierEditCitySearch("");
    setSupplierEdit({
      id: s.id,
      name: s.name || "",
      email: s.email || "",
      phone: s.phone || "",
      company_name: s.company_name || "",
      password: "",
      supplier_type: s.supplier_type || "hybrid",
      is_active: s.is_active !== false,
      cities: [...(s.cities_served || [])],
      productLines: parsed.map((row) => ({
        product_id: row.product_id,
        quantityStr:
          row.quantity != null && row.quantity >= 1 ? String(row.quantity) : "1",
      })),
    });
  };

  const saveSupplierEdit = async () => {
    if (!supplierEdit) return;
    if (!supplierEdit.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!supplierEdit.email.trim()) {
      toast.error("Email is required");
      return;
    }
    for (let i = 0; i < supplierEdit.productLines.length; i++) {
      const ln = supplierEdit.productLines[i];
      const n = parseInt(ln.quantityStr, 10);
      if (ln.quantityStr === "" || !Number.isFinite(n) || n < 1) {
        toast.error(`Product line ${i + 1}: quantity must be at least 1`);
        return;
      }
    }
    if (supplierEdit.password && supplierEdit.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSupplierEditSaving(true);
    try {
      const body = {
        name: supplierEdit.name.trim(),
        email: supplierEdit.email.trim(),
        phone: supplierEdit.phone.trim(),
        company_name: supplierEdit.company_name.trim(),
        supplier_type: supplierEdit.supplier_type,
        is_active: supplierEdit.is_active,
        cities_served: supplierEdit.cities,
        products_supplied: supplierEdit.productLines.map((l) => ({
          product_id: l.product_id,
          quantity: parseInt(l.quantityStr, 10),
        })),
      };
      if (supplierEdit.password) body.password = supplierEdit.password;
      await api.put(`/admin/suppliers/${supplierEdit.id}`, body);
      toast.success("Supplier updated");
      setSupplierEdit(null);
      fetchAll();
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update supplier");
    }
    setSupplierEditSaving(false);
  };

  const normalizeSupplierEditCity = (value) => String(value || "").trim().replace(/\s+/g, " ");

  const addSupplierEditCityFromSearch = useCallback((city) => {
    const normalized = normalizeSupplierEditCity(city);
    if (!normalized) return;
    setSupplierEdit((prev) => {
      if (!prev) return prev;
      if (prev.cities.some((c) => c.toLowerCase() === normalized.toLowerCase())) return prev;
      return { ...prev, cities: [...prev.cities, normalized] };
    });
    setSupplierEditCitySearch("");
  }, []);

  const removeSupplierEditCity = (city) => {
    setSupplierEdit((prev) => {
      if (!prev) return prev;
      return { ...prev, cities: prev.cities.filter((c) => c !== city) };
    });
  };

  const toggleSupplierEditProduct = (productId) => {
    setSupplierEdit((prev) => {
      if (!prev) return prev;
      const i = prev.productLines.findIndex((l) => l.product_id === productId);
      if (i >= 0) {
        return { ...prev, productLines: prev.productLines.filter((_, idx) => idx !== i) };
      }
      return {
        ...prev,
        productLines: [...prev.productLines, { product_id: productId, quantityStr: "1" }],
      };
    });
  };

  const setSupplierEditLineQty = (productId, raw) => {
    const digits = String(raw).replace(/\D/g, "");
    setSupplierEdit((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        productLines: prev.productLines.map((l) =>
          l.product_id === productId ? { ...l, quantityStr: digits } : l
        ),
      };
    });
  };

  const confirmDeleteSupplier = async () => {
    if (!supplierDeleteTarget) return;
    setSupplierDeleteLoading(true);
    try {
      await api.delete(`/admin/suppliers/${supplierDeleteTarget.id}`);
      toast.success("Supplier deactivated");
      setSupplierDeleteTarget(null);
      fetchAll();
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to deactivate supplier");
    }
    setSupplierDeleteLoading(false);
  };

  const confirmDeleteLead = async () => {
    if (!leadDeleteTarget) return;
    const deletedId = leadDeleteTarget.id;
    setLeadDeleteLoading(true);
    try {
      await api.delete(`/admin/leads/${deletedId}`);
      toast.success("Lead deleted");
      setLeadDeleteTarget(null);
      setLeads((prev) => prev.filter((lead) => lead.id !== deletedId));
      setLeadsTotal((prev) => Math.max(0, Number(prev || 0) - 1));
      setSelectedLeadIds((prev) => {
        const next = new Set(prev);
        next.delete(deletedId);
        return next;
      });
      const remainingOnPage = leads.filter((lead) => lead.id !== deletedId).length;
      if (remainingOnPage === 0 && leadPage > 1) {
        setLeadPage((p) => Math.max(1, p - 1));
      } else {
        fetchLeads();
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete lead");
    }
    setLeadDeleteLoading(false);
  };

  const addProduct = async () => {
    if (!newProduct.name) { toast.error("Enter product name"); return; }
    try {
      let imageUrl = (newProduct.image_url || "").trim();
      if (newProductImageFile) {
        imageUrl = await uploadAdminProductImage(newProductImageFile);
      }
      await api.post("/products", {
        name: newProduct.name,
        category: newProduct.category,
        image_url: imageUrl,
        sizes: formatProductSizes(newProduct.sizeList),
      });
      toast.success("Product added");
      setProductDialog(false);
      setNewProductImageFile(null);
      setNewProduct({ name: "", category: "General", image_url: "", sizeList: [] });
      fetchAll();
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || "Failed to add product");
    }
  };

  const deleteProduct = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      fetchAll();
    } catch (err) { toast.error("Failed to delete"); }
  };

  const openBlogCreate = () => {
    setBlogEditId(null);
    setBlogForm({ title: "", slug: "", excerpt: "", body: "", published: false });
    setBlogDialogOpen(true);
  };

  const openBlogEdit = (post) => {
    setBlogEditId(post.id);
    setBlogForm({
      title: post.title || "",
      slug: post.slug || "",
      excerpt: post.excerpt || "",
      body: post.body || "",
      published: !!post.published,
    });
    setBlogDialogOpen(true);
  };

  const deleteBlogPost = async (id) => {
    if (!window.confirm("Delete this blog post permanently?")) return;
    try {
      await api.delete(`/admin/blog-posts/${id}`);
      toast.success("Post deleted");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete post");
    }
  };

  const saveBlogPost = async () => {
    const title = blogForm.title.trim();
    if (!title) {
      toast.error("Title is required");
      return;
    }
    setBlogSaving(true);
    try {
      if (blogEditId) {
        await api.put(`/admin/blog-posts/${blogEditId}`, {
          title,
          slug: blogForm.slug.trim(),
          excerpt: blogForm.excerpt,
          body: blogForm.body,
          published: blogForm.published,
        });
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog-posts", {
          title,
          slug: blogForm.slug.trim() || undefined,
          excerpt: blogForm.excerpt,
          body: blogForm.body,
          published: blogForm.published,
        });
        toast.success("Post created");
      }
      setBlogDialogOpen(false);
      fetchAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : err.message || "Save failed");
    }
    setBlogSaving(false);
  };

  const saveEditProduct = async () => {
    if (!productEdit?.id) return;
    const name = String(productEdit.name || "").trim();
    if (!name) {
      toast.error("Enter product name");
      return;
    }
    try {
      let imageUrl = String(productEdit.image_url || "").trim();
      if (productEditImageFile) {
        imageUrl = await uploadAdminProductImage(productEditImageFile);
      }
      await api.put(`/products/${productEdit.id}`, {
        name,
        category: productEdit.category || "General",
        image_url: imageUrl,
        sizes: formatProductSizes(productEdit.sizeList || []),
      });
      toast.success("Product saved");
      setProductEdit(null);
      setProductEditImageFile(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save product");
    }
  };

  /** Catalog Add/Edit product: only subcategories (rows with a parent). */
  const productSubcategoryOptions = useMemo(() => {
    const subs = (productCategories || []).filter((c) => c?.id && normCategoryParentId(c.parent_id));
    const names = sortCategoriesByOrderThenName(subs).map((c) => c.name).filter(Boolean);
    return names.length > 0 ? names : ["General"];
  }, [productCategories]);

  const editProductCategoryChoices = useMemo(() => {
    const base = productSubcategoryOptions;
    const cur = productEdit?.category;
    if (cur && !base.includes(cur)) return [...base, cur];
    return base;
  }, [productSubcategoryOptions, productEdit?.category]);

  const designatedParentCategories = useMemo(() => {
    const all = Array.isArray(productCategories) ? productCategories : [];
    return all.filter((c) => c?.id && c.is_parent_group === true && !normCategoryParentId(c.parent_id));
  }, [productCategories]);

  const parentGroupsTableRows = useMemo(
    () => sortCategoriesByOrderThenName(designatedParentCategories),
    [designatedParentCategories]
  );

  const subcategoriesTableRows = useMemo(() => {
    const subs = (productCategories || []).filter((c) => c?.id && normCategoryParentId(c.parent_id));
    return [...subs].sort((a, b) => {
      const pa = productCategories.find((x) => x.id === a.parent_id)?.name || "";
      const pb = productCategories.find((x) => x.id === b.parent_id)?.name || "";
      const byParent = pa.localeCompare(pb);
      if (byParent !== 0) return byParent;
      const ao = Number.isFinite(Number(a.sort_order)) ? Number(a.sort_order) : 9999;
      const bo = Number.isFinite(Number(b.sort_order)) ? Number(b.sort_order) : 9999;
      if (ao !== bo) return ao - bo;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [productCategories]);

  const otherTopLevelTableRows = useMemo(() => {
    const rows = (productCategories || []).filter(
      (c) => c?.id && !normCategoryParentId(c.parent_id) && c.is_parent_group !== true
    );
    return sortCategoriesByOrderThenName(rows);
  }, [productCategories]);

  const categoryDialogShowsParentField = useMemo(() => {
    if (categoryDialogMode === "create-parent") return false;
    if (categoryDialogMode === "create-sub") return true;
    if (categoryDialogMode === "edit") return !!normCategoryParentId(categoryForm.parent_id);
    return false;
  }, [categoryDialogMode, categoryForm.parent_id]);

  const categoryDialogShowsParentGroupCheckbox = useMemo(() => {
    return categoryDialogMode === "edit" && !normCategoryParentId(categoryForm.parent_id);
  }, [categoryDialogMode, categoryForm.parent_id]);

  useEffect(() => {
    if (categoryDialogMode !== "create-parent" && categoryDialogMode !== "create-sub") return;
    const pid = normCategoryParentId(categoryForm.parent_id);
    const siblings = productCategories.filter((c) => normCategoryParentId(c.parent_id) === pid);
    let max = -1;
    for (const s of siblings) {
      const so = Number(s.sort_order);
      if (Number.isFinite(so) && so > max) max = so;
    }
    setCategoryForm((prev) => ({ ...prev, sort_order: max + 1 }));
  }, [categoryDialogMode, categoryForm.parent_id, productCategories]);

  const openCategoryCreateParent = () => {
    setCategoryForm({
      id: "",
      name: "",
      parent_id: "",
      type: "rent",
      tier: "listed",
      is_active: true,
      sort_order: 0,
      is_parent_group: true,
    });
    setCategoryDialogMode("create-parent");
  };

  const openCategoryCreateSub = () => {
    if (designatedParentCategories.length === 0) {
      toast.error("Open “Parent categories” and add at least one parent group first");
      return;
    }
    setCategoryForm({
      id: "",
      name: "",
      parent_id: "",
      type: "rent",
      tier: "listed",
      is_active: true,
      sort_order: 0,
      is_parent_group: false,
    });
    setCategoryDialogMode("create-sub");
  };

  const openCategoryEdit = (c) => {
    const name = String(c?.name || "");
    setCategoryForm({
      id: String(c?.id || ""),
      name,
      parent_id: normCategoryParentId(c?.parent_id),
      type: String(c?.type || "rent"),
      tier: String(c?.tier || "listed"),
      is_active: c?.is_active !== false,
      sort_order: Number.isFinite(Number(c?.sort_order)) ? Number(c.sort_order) : 0,
      is_parent_group: c?.is_parent_group === true,
    });
    setCategoryDialogMode("edit");
  };

  const saveCategory = async () => {
    const name = String(categoryForm.name || "").trim();
    if (!name) {
      toast.error("Name is required");
      return;
    }
    const slug = slugifyName(name);
    let parentRaw = normCategoryParentId(categoryForm.parent_id);
    if (categoryDialogMode === "create-parent") {
      parentRaw = "";
    }
    if (categoryDialogMode === "create-sub") {
      if (!parentRaw) {
        toast.error("Choose a parent category (top-level groups only)");
        return;
      }
      const validParent = designatedParentCategories.some((p) => p.id === parentRaw);
      if (!validParent) {
        toast.error("Choose a designated parent group from Parent categories");
        return;
      }
    }
    if (categoryDialogMode === "edit" && !categoryDialogShowsParentField) {
      parentRaw = "";
    }
    const payload = {
      name,
      slug,
      type: String(categoryForm.type || "rent"),
      tier: String(categoryForm.tier || "listed"),
      is_active: categoryForm.is_active === true,
      sort_order: Number.isFinite(Number(categoryForm.sort_order)) ? Number(categoryForm.sort_order) : 0,
      parent_id: parentRaw || null,
    };
    if (categoryDialogMode === "create-parent") {
      payload.is_parent_group = true;
    } else if (categoryDialogMode === "create-sub") {
      payload.is_parent_group = false;
    } else if (categoryDialogMode === "edit" && !normCategoryParentId(categoryForm.parent_id)) {
      payload.is_parent_group = categoryForm.is_parent_group === true;
    }
    setCategorySaving(true);
    try {
      if (categoryDialogMode === "create-parent" || categoryDialogMode === "create-sub") {
        await api.post("/admin/product-categories", payload);
        toast.success(categoryDialogMode === "create-parent" ? "Parent category created" : "Subcategory created");
      } else if (categoryDialogMode === "edit" && categoryForm.id) {
        await api.put(`/admin/product-categories/${categoryForm.id}`, payload);
        toast.success("Category saved");
      }
      setCategoryDialogMode(null);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save category");
    }
    setCategorySaving(false);
  };

  const deleteCategory = async (id) => {
    if (!id) {
      toast.error("Cannot delete this category (missing id). Refresh the page.");
      return;
    }
    setCategoryActionLoading(true);
    try {
      await api.delete(`/admin/product-categories/${encodeURIComponent(id)}`);
      toast.success("Category removed");
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
    setCategoryActionLoading(false);
  };

  const toggleCategoryActive = async (c) => {
    const id = c?.id;
    if (!id) return;
    setCategoryActionLoading(true);
    try {
      await api.put(`/admin/product-categories/${id}`, { is_active: !(c?.is_active !== false) });
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setCategoryActionLoading(false);
  };

  const reorderCategories = async (nextIds, parentKey) => {
    setCategoryActionLoading(true);
    try {
      await api.post("/admin/product-categories/reorder", {
        ids: nextIds,
        parent_id: parentKey ? parentKey : null,
      });
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reorder");
    }
    setCategoryActionLoading(false);
  };

  const moveCategory = (id, dir) => {
    const row = productCategories.find((x) => x.id === id);
    if (!row) return;
    const pkey = normCategoryParentId(row.parent_id);
    const siblings = productCategories
      .filter((x) => normCategoryParentId(x.parent_id) === pkey)
      .sort((a, b) => {
        const ao = Number.isFinite(Number(a.sort_order)) ? Number(a.sort_order) : 9999;
        const bo = Number.isFinite(Number(b.sort_order)) ? Number(b.sort_order) : 9999;
        if (ao !== bo) return ao - bo;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
    const ids = siblings.map((x) => x.id);
    const idx = ids.indexOf(id);
    if (idx < 0) return;
    const swapWith = dir === "up" ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= ids.length) return;
    const next = [...ids];
    const tmp = next[idx];
    next[idx] = next[swapWith];
    next[swapWith] = tmp;
    reorderCategories(next, pkey);
  };

  const categoryTableEmptyColSpan = (showParentColumn) => {
    if (isAgent) return showParentColumn ? 8 : 7;
    return showParentColumn ? 9 : 8;
  };

  const renderCategoryTableRow = (c, showParentColumn, serial) => {
    const parentName = c.parent_id
      ? productCategories.find((x) => x.id === c.parent_id)?.name
      : null;
    return (
      <TableRow key={c.id} className="border-neutral-100" data-testid={`category-row-${c.id}`}>
        <TableCell className="text-xs font-mono text-neutral-500">{serial}</TableCell>
        <TableCell className="font-medium text-sm">{c.name}</TableCell>
        {showParentColumn ? (
          <TableCell className="text-xs text-neutral-600 max-w-[160px]">
            {parentName ? <span className="truncate block">{parentName}</span> : "—"}
          </TableCell>
        ) : null}
        <TableCell className="text-xs font-mono text-neutral-600">{c.slug || "—"}</TableCell>
        <TableCell>
          <Badge variant="secondary" className="rounded-sm text-xs capitalize">
            {c.type || "rent"}
          </Badge>
        </TableCell>
        <TableCell>
          <Badge variant="secondary" className="rounded-sm text-xs capitalize">
            {c.tier || "listed"}
          </Badge>
        </TableCell>
        <TableCell>
          {c.is_active === false ? (
            <Badge className="rounded-sm text-xs bg-neutral-200 text-neutral-700 border border-neutral-400">
              No
            </Badge>
          ) : (
            <Badge className="rounded-sm text-xs bg-green-100 text-green-800 border border-green-300">
              Yes
            </Badge>
          )}
        </TableCell>
        <TableCell className="text-xs font-mono text-neutral-600 tabular-nums">{c.sort_order ?? 0}</TableCell>
        {!isAgent && (
          <TableCell>
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm text-xs h-7 border-neutral-300"
                onClick={() => openCategoryEdit(c)}
                data-testid={`edit-category-${c.id}`}
              >
                <PencilSimple size={12} className="mr-0.5" /> Edit
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm text-xs h-7 border-neutral-300"
                onClick={() => toggleCategoryActive(c)}
                data-testid={`toggle-category-${c.id}`}
              >
                {c.is_active === false ? "Activate" : "Deactivate"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm text-xs h-7 border-neutral-300"
                onClick={() => moveCategory(c.id, "up")}
                data-testid={`move-up-category-${c.id}`}
                title="Move up within same group"
              >
                <CaretUp size={12} />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm text-xs h-7 border-neutral-300"
                onClick={() => moveCategory(c.id, "down")}
                data-testid={`move-down-category-${c.id}`}
                title="Move down within same group"
              >
                <CaretDown size={12} />
              </Button>
              <Button
                data-testid={`delete-category-${c.id}`}
                size="sm"
                variant="outline"
                onClick={() => deleteCategory(c.id)}
                disabled={!c.id}
                title={!c.id ? "Refresh page — category id missing" : "Delete category"}
                className="rounded-sm text-xs h-7 border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash size={12} />
              </Button>
            </div>
          </TableCell>
        )}
      </TableRow>
    );
  };

  const categoryBusy = categorySaving || categoryActionLoading;

  const addSP = async () => {
    if (!newSP.name) { toast.error("Enter name"); return; }
    try {
      await api.post("/service-providers", newSP);
      toast.success("Service provider added");
      setSpDialog(false);
      setNewSP({ name: "", category: "labor", phone: "", city: "", description: "" });
      fetchAll();
    } catch (err) { toast.error("Failed to add"); }
  };

  const deleteSP = async (id) => {
    try {
      await api.delete(`/service-providers/${id}`);
      toast.success("Deleted");
      fetchAll();
    } catch (err) { toast.error("Failed to delete"); }
  };

  const approveProductRequest = async (id) => {
    try {
      await api.post(`/admin/product-requests/${id}/approve`);
      toast.success("Product approved and added to catalog");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    }
  };

  const openRejectProductRequest = (id) => {
    setRejectDialog(id);
    setRejectNote("");
  };

  const confirmRejectProductRequest = async () => {
    if (!rejectDialog) return;
    try {
      await api.post(`/admin/product-requests/${rejectDialog}/reject`, { note: rejectNote });
      toast.success("Request rejected");
      setRejectDialog(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    }
  };

  const filteredLeads = leads;

  const visibleLeadIds = useMemo(
    () => filteredLeads.map((l) => l.id).filter(Boolean),
    [filteredLeads]
  );

  const visibleSelectionState = useMemo(() => {
    if (!visibleLeadIds.length) return { checked: false, selectedCount: 0 };
    let selectedCount = 0;
    for (const id of visibleLeadIds) {
      if (selectedLeadIds.has(id)) selectedCount += 1;
    }
    if (selectedCount === 0) return { checked: false, selectedCount };
    if (selectedCount === visibleLeadIds.length) return { checked: true, selectedCount };
    return { checked: "indeterminate", selectedCount };
  }, [selectedLeadIds, visibleLeadIds]);

  const toggleSelectAllVisibleLeads = (nextChecked) => {
    const shouldSelect = nextChecked === true || nextChecked === "indeterminate";
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (!shouldSelect) {
        for (const id of visibleLeadIds) next.delete(id);
        return next;
      }
      for (const id of visibleLeadIds) next.add(id);
      return next;
    });
  };

  const toggleLeadSelected = (leadId, nextChecked) => {
    if (!leadId) return;
    const shouldSelect = nextChecked === true || nextChecked === "indeterminate";
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (shouldSelect) next.add(leadId);
      else next.delete(leadId);
      return next;
    });
  };

  const openBulkLeadDelete = () => {
    if (isAgent) return;
    const ids = visibleLeadIds.filter((id) => selectedLeadIds.has(id));
    if (ids.length === 0) {
      toast.error("Select at least one lead");
      return;
    }
    setBulkLeadDeleteOpen(true);
  };

  const confirmBulkDeleteLeads = async () => {
    const ids = visibleLeadIds.filter((id) => selectedLeadIds.has(id));
    if (ids.length === 0) {
      setBulkLeadDeleteOpen(false);
      return;
    }
    setBulkLeadDeleteLoading(true);
    try {
      await api.post("/admin/leads/bulk-delete", { ids });
      const idSet = new Set(ids);
      toast.success(`Deleted ${ids.length} lead${ids.length === 1 ? "" : "s"}`);
      setLeads((prev) => prev.filter((lead) => !idSet.has(lead.id)));
      setLeadsTotal((prev) => Math.max(0, Number(prev || 0) - ids.length));
      setSelectedLeadIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
      setBulkLeadDeleteOpen(false);
      const remainingOnPage = leads.filter((lead) => !idSet.has(lead.id)).length;
      if (remainingOnPage === 0 && leadPage > 1) {
        setLeadPage((p) => Math.max(1, p - 1));
      } else {
        fetchLeads();
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete leads");
    }
    setBulkLeadDeleteLoading(false);
  };

  const pendingPrCount = productRequests.filter((r) => r.status === "pending").length;
  const filteredProductRequests =
    prFilter === "all" ? productRequests : productRequests.filter((r) => r.status === prFilter);

  const tabOptions = useMemo(() => {
    /** @type {{ value: string, label: string }[]} */
    const base = [
      { value: "overview", label: "Overview" },
      { value: "users", label: "Users" },
      { value: "leads", label: "Leads" },
      ...(!isAgent ? [{ value: "suppliers", label: "Suppliers" }] : []),
      { value: "categories", label: "Categories" },
      { value: "product-requests", label: `Catalog requests${pendingPrCount > 0 ? ` (${pendingPrCount})` : ""}` },
      { value: "products", label: "Products" },
      { value: "blog", label: "Blog" },
      { value: "old-material", label: "Old material" },
      ...(!isAgent ? [{ value: "settings", label: "Settings" }] : []),
    ];
    return base;
  }, [isAgent, pendingPrCount]);

  const assignLeadMeta = useMemo(() => {
    if (!assignDialog?.items?.length) {
      return { leadProductIds: new Set(), resolveItemProductId: () => null };
    }
    const norm = (s) => (s || "").trim().toLowerCase();
    const nameToId = new Map();
    (products || []).forEach((p) => {
      if (p?.id && p?.name) nameToId.set(norm(p.name), p.id);
    });
    const resolveItemProductId = (item) => {
      if (item.product_id) return item.product_id;
      return nameToId.get(norm(item.product_name)) || null;
    };
    const ids = new Set();
    for (const item of assignDialog.items) {
      const pid = resolveItemProductId(item);
      if (pid) ids.add(pid);
    }
    return { leadProductIds: ids, resolveItemProductId };
  }, [assignDialog, products]);

  const { leadProductIds, resolveItemProductId } = assignLeadMeta;

  const activeAssignSuppliers = useMemo(
    () => assignPickerSuppliers.filter(isSupplierActive),
    [assignPickerSuppliers]
  );

  const activeTopSuppliers = useMemo(
    () => (metrics.top_suppliers || []).filter(isSupplierActive),
    [metrics.top_suppliers]
  );

  const supplierMatchesLeadProducts = (s) => {
    const lines = parseProductsSupplied(s.products_supplied);
    return lines.some((l) => leadProductIds.has(l.product_id));
  };

  const citySuppliers = assignDialog
    ? activeAssignSuppliers.filter((s) => s.cities_served?.includes(assignDialog.city))
    : [];
  const citySupplierIds = new Set(citySuppliers.map((s) => s.id));
  const productMatchSuppliers = assignDialog
    ? activeAssignSuppliers.filter(
        (s) => supplierMatchesLeadProducts(s) && !citySupplierIds.has(s.id)
      )
    : [];
  const otherSuppliers = assignDialog
    ? activeAssignSuppliers.filter(
        (s) =>
          !s.cities_served?.includes(assignDialog.city) &&
          !supplierMatchesLeadProducts(s)
      )
    : [];

  const assignSupplierSearchQuery = normalizeAssignSearchText(assignSupplierSearch);
  const assignSearchTyped = assignSupplierSearch.trim();
  const assignIsApiSearch = Boolean(assignSearchTyped);
  const assignSearchReady = assignIsApiSearch && assignSearchTyped === assignSupplierSearchQ;
  const assignSearchBusy = assignIsApiSearch && (!assignSearchReady || assignSearchLoading);

  const assignSupplierMatchGroup = (s) => {
    if (assignDialog?.city && s.cities_served?.includes(assignDialog.city)) return "city";
    if (supplierMatchesLeadProducts(s)) return "product";
    return "other";
  };

  const assignSupplierRows = useMemo(() => {
    if (assignIsApiSearch) {
      if (!assignSearchReady) return [];
      return assignSearchItems.map((s) => ({ ...s, matchGroup: assignSupplierMatchGroup(s) }));
    }
    return [
      ...citySuppliers.map((s) => ({ ...s, matchGroup: "city" })),
      ...productMatchSuppliers.map((s) => ({ ...s, matchGroup: "product" })),
      ...otherSuppliers.map((s) => ({ ...s, matchGroup: "other" })),
    ];
  }, [
    assignIsApiSearch,
    assignSearchReady,
    assignSearchItems,
    assignDialog,
    citySuppliers,
    productMatchSuppliers,
    otherSuppliers,
    leadProductIds,
  ]);

  const assignListPageSize = assignIsApiSearch ? ASSIGN_SUPPLIER_SEARCH_PAGE_SIZE : ASSIGN_SUPPLIER_PAGE_SIZE;
  const assignSupplierListTotal = assignIsApiSearch ? (assignSearchReady ? assignSearchTotal : 0) : assignSupplierRows.length;
  const assignSupplierPageCount = Math.max(1, Math.ceil(assignSupplierListTotal / assignListPageSize));
  const assignSupplierPageSafe = Math.min(Math.max(1, assignSupplierPage), assignSupplierPageCount);
  const assignSupplierPageRows = useMemo(() => {
    if (assignIsApiSearch) return assignSupplierRows;
    const start = (assignSupplierPageSafe - 1) * ASSIGN_SUPPLIER_PAGE_SIZE;
    return assignSupplierRows.slice(start, start + ASSIGN_SUPPLIER_PAGE_SIZE);
  }, [assignIsApiSearch, assignSupplierRows, assignSupplierPageSafe]);

  useEffect(() => {
    setAssignSupplierPage(1);
  }, [assignDialog?.id]);

  useEffect(() => {
    if (assignSupplierPage > assignSupplierPageCount) {
      setAssignSupplierPage(assignSupplierPageCount);
    }
  }, [assignSupplierPage, assignSupplierPageCount]);

  const minRequired = 1;

  const assignDialogAssignments = assignDialog?.assignments || [];
  const assignListedSupplierIds = useMemo(() => {
    const ids = [
      ...citySuppliers.map((s) => s.id),
      ...productMatchSuppliers.map((s) => s.id),
      ...otherSuppliers.map((s) => s.id),
    ];
    return new Set(ids);
  }, [citySuppliers, productMatchSuppliers, otherSuppliers]);
  const assignOrphanAssignments = assignDialog
    ? assignDialogAssignments.filter((a) => a.supplier_id && !assignListedSupplierIds.has(a.supplier_id))
    : [];
  const filteredAssignOrphanAssignments = useMemo(() => {
    if (!assignSupplierSearchQuery) return assignOrphanAssignments;
    return assignOrphanAssignments.filter((a) => {
      const linked = activeAssignSuppliers.find((s) => s.id === a.supplier_id);
      return supplierMatchesAssignSearch(
        {
          name: a.supplier_name || a.supplier_id,
          company_name: linked?.company_name,
          cities_served: linked?.cities_served,
        },
        assignSupplierSearchQuery
      );
    });
  }, [assignOrphanAssignments, assignSupplierSearchQuery, activeAssignSuppliers]);

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-neutral-200" data-testid="admin-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <HardHat size={28} weight="bold" className="text-orange-600" />
            <span className="text-xl font-bold tracking-tight">SCAFFOINDIA</span>
            <Badge className={`rounded-sm text-xs ml-2 border ${isAgent ? "bg-violet-100 text-violet-900 border-violet-300" : "bg-red-100 text-red-800 border-red-300"}`}>
              {isAgent ? "STAFF" : "ADMIN"}
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            {categoryBusy && (
              <div className="inline-flex items-center gap-2 text-xs text-neutral-500" data-testid="category-header-loader">
                <CircleNotch size={16} className="animate-spin" aria-hidden />
                <span className="hidden sm:inline">Working…</span>
              </div>
            )}
            <span className="text-sm text-neutral-600 hidden sm:block">{user?.name}</span>
            <Button data-testid="admin-logout-btn" variant="outline" size="sm" onClick={() => { logout(); navigate(isAgent ? "/staff/login" : "/"); }} className="rounded-sm border-neutral-300">
              <SignOut size={16} weight="bold" className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <div className="sm:hidden mb-4">
            <Select
              value={tabOptions.some((t) => t.value === tab) ? tab : "overview"}
              onValueChange={setTab}
            >
              <SelectTrigger className="rounded-sm border-neutral-300 bg-white" data-testid="admin-tab-select">
                <SelectValue placeholder="Select section" />
              </SelectTrigger>
              <SelectContent side="bottom" align="start" sideOffset={6} className="max-h-[320px]">
                {tabOptions.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <TabsList className="hidden sm:flex bg-white border border-neutral-200 rounded-sm h-11 mb-8 p-1 flex-wrap h-auto gap-1">
            <TabsTrigger data-testid="tab-overview" value="overview" className="rounded-sm text-xs sm:text-sm font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">
              <ChartBar size={16} className="mr-1 hidden sm:inline" /> Overview
            </TabsTrigger>
            <TabsTrigger data-testid="tab-users" value="users" className="rounded-sm text-xs sm:text-sm font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">
              <UserPlus size={16} className="mr-1 hidden sm:inline" /> Users
            </TabsTrigger>
            <TabsTrigger data-testid="tab-leads" value="leads" className="rounded-sm text-xs sm:text-sm font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">
              <ListChecks size={16} className="mr-1 hidden sm:inline" /> Leads
            </TabsTrigger>
            {!isAgent && (
              <TabsTrigger data-testid="tab-suppliers" value="suppliers" className="rounded-sm text-xs sm:text-sm font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">
                <Users size={16} className="mr-1 hidden sm:inline" /> Suppliers
              </TabsTrigger>
            )}
            <TabsTrigger data-testid="tab-categories" value="categories" className="rounded-sm text-xs sm:text-sm font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">
              <Folders size={16} className="mr-1 hidden sm:inline" /> Categories
            </TabsTrigger>
            <TabsTrigger data-testid="tab-product-requests" value="product-requests" className="rounded-sm text-xs sm:text-sm font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">
              <ClipboardText size={16} className="mr-1 hidden sm:inline" /> Catalog requests
              {pendingPrCount > 0 && (
                <Badge className="ml-1 rounded-sm bg-amber-100 text-amber-900 border border-amber-300 text-[10px] px-1.5 py-0">{pendingPrCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-products" value="products" className="rounded-sm text-xs sm:text-sm font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">
              <Package size={16} className="mr-1 hidden sm:inline" /> Products
            </TabsTrigger>
            <TabsTrigger data-testid="tab-blog" value="blog" className="rounded-sm text-xs sm:text-sm font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">
              <Newspaper size={16} className="mr-1 hidden sm:inline" /> Blog
            </TabsTrigger>
            <TabsTrigger data-testid="tab-old-material" value="old-material" className="rounded-sm text-xs sm:text-sm font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">
              <Package size={16} className="mr-1 hidden sm:inline" /> Old material
            </TabsTrigger>
            {!isAgent && (
              <TabsTrigger data-testid="tab-settings" value="settings" className="rounded-sm text-xs sm:text-sm font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">
                <Gear size={16} className="mr-1 hidden sm:inline" /> Settings
              </TabsTrigger>
            )}
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" data-testid="overview-tab">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Total Leads", value: metrics.total_leads || 0, color: "text-neutral-900" },
                { label: "Verified", value: metrics.verified || 0, color: "text-green-600" },
                { label: "Not Reachable", value: metrics.not_reachable || 0, color: "text-orange-600" },
                { label: "Fake", value: metrics.fake || 0, color: "text-red-600" },
                { label: "Pending", value: metrics.pending || 0, color: "text-yellow-600" },
                { label: "Quotations Sent", value: metrics.total_quotations || 0, color: "text-violet-600" },
                { label: "Won Leads", value: metrics.total_won || 0, color: "text-green-600" },
                { label: "Total Suppliers", value: metrics.total_suppliers || 0, color: "text-blue-600" },
              ].map((m, i) => (
                <Card key={i} className="rounded-sm border-neutral-200" data-testid={`metric-${m.label.toLowerCase().replace(/\s/g, "-")}`}>
                  <CardContent className="p-4 text-center">
                    <p className={`text-3xl font-black ${m.color}`}>{m.value}</p>
                    <p className="text-xs text-neutral-500 font-mono uppercase tracking-wider mt-1">{m.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Top Suppliers */}
            {activeTopSuppliers.length > 0 && (
              <Card className="rounded-sm border-neutral-200">
                <CardContent className="p-0">
                  <div className="px-4 py-3 border-b border-neutral-200 flex items-center gap-2">
                    <span className="font-bold text-sm">Top Suppliers</span>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-neutral-200">
                        <TableHead className="text-xs font-mono uppercase w-12">#</TableHead>
                        <TableHead className="text-xs font-mono uppercase">Name</TableHead>
                        <TableHead className="text-xs font-mono uppercase">Won</TableHead>
                        <TableHead className="text-xs font-mono uppercase">Score</TableHead>
                        <TableHead className="text-xs font-mono uppercase">Cities</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeTopSuppliers.map((s, i) => (
                        <TableRow key={i} className="border-neutral-100">
                          <TableCell className="text-xs font-mono text-neutral-500">{i + 1}</TableCell>
                          <TableCell className="font-medium text-sm">{s.name}</TableCell>
                          <TableCell className="font-mono text-sm">{s.total_won}</TableCell>
                          <TableCell className="font-mono text-sm">{s.performance_score}%</TableCell>
                          <TableCell><div className="flex flex-wrap gap-1">{(s.cities_served || []).slice(0, 3).map(c => <Badge key={c} variant="secondary" className="rounded-sm text-xs">{c}</Badge>)}</div></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* SETTINGS (ADMIN ONLY) */}
          <TabsContent value="settings" data-testid="settings-tab">
            <div className="max-w-xl space-y-6">
              <div>
                <h3 className="font-bold text-lg">Platform settings</h3>
                <p className="text-sm text-neutral-500 mt-1">
                  Leads, staff assignment, and WhatsApp notifications. Only administrators can change these options.
                </p>
              </div>
              <Card className="rounded-sm border-neutral-200">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between gap-4 rounded-sm border border-neutral-200 bg-white px-3 py-3">
                    <div>
                      <Label htmlFor="auto-assign-agents" className="text-sm font-medium cursor-pointer">
                        Auto-assign new leads
                      </Label>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        New leads rotate across active staff users when enabled.
                      </p>
                    </div>
                    <Switch
                      id="auto-assign-agents"
                      checked={!!appSettings.auto_assign_agents}
                      onCheckedChange={toggleAutoAssignAgents}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-sm border border-neutral-200 bg-white px-3 py-3">
                    <div>
                      <Label htmlFor="wa-notify-suppliers" className="text-sm font-medium cursor-pointer">
                        WhatsApp: suppliers
                      </Label>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        Send a template when verified leads are assigned to suppliers.
                      </p>
                    </div>
                    <Switch
                      id="wa-notify-suppliers"
                      checked={!!appSettings.whatsapp_notify_suppliers}
                      onCheckedChange={toggleWhatsAppSuppliers}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-sm border border-neutral-200 bg-white px-3 py-3">
                    <div>
                      <Label htmlFor="wa-notify-staff" className="text-sm font-medium cursor-pointer">
                        WhatsApp: staff
                      </Label>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        Send a template when the assigned staff user on a lead changes.
                      </p>
                    </div>
                    <Switch
                      id="wa-notify-staff"
                      checked={!!appSettings.whatsapp_notify_staff}
                      onCheckedChange={toggleWhatsAppStaff}
                    />
                  </div>
                </CardContent>
              </Card>
              <p className="text-xs text-neutral-500">
                WhatsApp requires env credentials, Meta-approved templates, and a <strong>phone</strong> number on each supplier or staff account. If <strong>WhatsApp: staff</strong> is on, changing <strong>Assigned user</strong> on a lead queues a message to the newly assigned person; check server logs if delivery fails.
              </p>
            </div>
          </TabsContent>

          {/* USERS (STAFF) TAB */}
          <TabsContent value="users" data-testid="users-tab">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h3 className="font-bold">Staff users</h3>
                <p className="text-sm text-neutral-500 mt-1">
                  Internal accounts for your team. New lead auto-assignment and WhatsApp toggles are under <strong className="text-neutral-700">Settings</strong>.
                  {!isAgent && " Suppliers are managed under Suppliers."}
                  {isAgent && " Only an administrator can add, edit, or deactivate staff accounts."}
                </p>
              </div>
              {!isAgent && (
                <Button type="button" size="sm" onClick={openStaffCreate} className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white shrink-0">
                  <UserPlus size={14} className="mr-1" /> Add user
                </Button>
              )}
            </div>
            <Card className="rounded-sm border-neutral-200 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-neutral-200 bg-neutral-50">
                      <TableHead className="text-xs font-mono uppercase w-12">#</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Email</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Name</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Phone</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Status</TableHead>
                      {!isAgent && <TableHead className="text-xs font-mono uppercase">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isAgent ? 5 : 6} className="text-center py-8 text-neutral-400">
                          No staff users yet. Add one to start round-robin assignment.
                        </TableCell>
                      </TableRow>
                    ) : (
                      agents.map((a, i) => (
                        <TableRow key={a.id} className="border-neutral-100" data-testid={`staff-row-${a.id}`}>
                          <TableCell className="text-xs font-mono text-neutral-500">{i + 1}</TableCell>
                          <TableCell className="text-sm font-mono">{a.email}</TableCell>
                          <TableCell className="text-sm">{a.name || "—"}</TableCell>
                          <TableCell className="text-sm font-mono">{a.phone || "—"}</TableCell>
                          <TableCell>
                            {a.is_active === false ? (
                              <Badge className="rounded-sm text-xs bg-neutral-200 text-neutral-700 border border-neutral-400">Inactive</Badge>
                            ) : (
                              <Badge className="rounded-sm text-xs bg-green-100 text-green-800 border border-green-300">Active</Badge>
                            )}
                          </TableCell>
                          {!isAgent && (
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="rounded-sm text-xs h-7"
                                  onClick={() => openStaffEdit(a)}
                                >
                                  <PencilSimple size={12} className="mr-0.5" /> Edit
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={a.is_active === false}
                                  className="rounded-sm text-xs h-7 border-red-300 text-red-700 hover:bg-red-50"
                                  onClick={() => setStaffDeleteTarget({ id: a.id, email: a.email })}
                                >
                                  <Trash size={12} className="mr-0.5" /> Deactivate
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* LEADS TAB */}
          <TabsContent value="leads" data-testid="leads-tab">
            {isAgent && (
              <p className="text-sm text-neutral-500 mb-4">
                Only leads assigned to you appear here. Leads with no staff assignee are visible to administrators only.
              </p>
            )}
            <div className="mb-6">
              <div className="sm:hidden">
                <Select
                  value={leadFilter}
                  onValueChange={(v) => {
                    setLeadFilter(v);
                    setLeadPage(1);
                    setSelectedLeadIds(new Set());
                  }}
                >
                  <SelectTrigger className="rounded-sm border-neutral-300 bg-white" data-testid="admin-lead-filter-select">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent side="bottom" align="start" sideOffset={6} className="max-h-[320px]">
                    {["all", "pending", "verified", "not_reachable", "fake"].map((s) => (
                      <SelectItem key={s} value={s}>
                        {s === "all" ? "All" : LEAD_STATUSES[s] || s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="hidden sm:flex flex-wrap gap-2">
                {["all", "pending", "verified", "not_reachable", "fake"].map((s) => (
                  <Button
                    key={s}
                    data-testid={`lead-filter-${s}`}
                    variant={leadFilter === s ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setLeadFilter(s);
                      setLeadPage(1);
                      setSelectedLeadIds(new Set());
                    }}
                    className={`rounded-sm text-xs ${leadFilter === s ? "bg-neutral-900 text-white" : "border-neutral-300"}`}
                  >
                    {s === "all" ? "All" : LEAD_STATUSES[s] || s}
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
              <div className="relative w-full sm:w-72">
                <MagnifyingGlass
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
                  aria-hidden
                />
                <Input
                  data-testid="lead-tab-search"
                  className="rounded-sm border-neutral-300 pl-9 pr-9 bg-white"
                  placeholder="Search by phone or city…"
                  value={leadSearchInput}
                  onChange={(e) => setLeadSearchInput(e.target.value)}
                  aria-label="Search leads by phone or city"
                />
                {leadSearchInput ? (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-sm text-neutral-500 hover:bg-neutral-100"
                    onClick={() => {
                      setLeadSearchInput("");
                      setLeadSearch("");
                      setLeadPage(1);
                      setSelectedLeadIds(new Set());
                    }}
                    aria-label="Clear lead search"
                  >
                    <X size={16} />
                  </button>
                ) : null}
              </div>
              {!isAgent ? (
                <div className="flex items-center justify-between gap-3 w-full sm:w-auto sm:ml-auto">
                  <div className="text-xs text-neutral-500">
                    Selected: <span className="font-mono">{visibleSelectionState.selectedCount}</span>
                    {visibleLeadIds.length ? (
                      <>
                        {" "}of{" "}
                        <span className="font-mono">{visibleLeadIds.length}</span>{" "}
                        visible
                      </>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="default"
                      onClick={openAddLead}
                      className="rounded-sm text-xs h-8 bg-neutral-900 hover:bg-neutral-700 text-white"
                      data-testid="add-lead-btn"
                    >
                      <Plus size={14} className="mr-1" /> Add Lead
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={visibleSelectionState.selectedCount === 0}
                      onClick={openBulkLeadDelete}
                      className="rounded-sm text-xs h-8 border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                      data-testid="bulk-delete-leads"
                    >
                      <Trash size={14} className="mr-1" /> Delete selected
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>

            <Card className="rounded-sm border-neutral-200 overflow-hidden">
              <div className="overflow-x-auto">
                <Table className="table-fixed min-w-[980px]">
                  <TableHeader>
                    <TableRow className="border-neutral-200 bg-neutral-50">
                      {!isAgent && (
                        <TableHead className="w-10 px-2">
                          <Checkbox
                            checked={visibleSelectionState.checked}
                            onCheckedChange={toggleSelectAllVisibleLeads}
                            aria-label="Select all visible leads"
                          />
                        </TableHead>
                      )}
                      <TableHead className="w-12 text-xs font-mono uppercase">#</TableHead>
                      <TableHead className="w-[108px] text-xs font-mono uppercase">Phone</TableHead>
                      <TableHead className="w-[72px] text-xs font-mono uppercase">Type</TableHead>
                      <TableHead className="w-[140px] text-xs font-mono uppercase">City</TableHead>
                      <TableHead className="w-[52px] text-xs font-mono uppercase">Items</TableHead>
                      <TableHead className="w-[120px] text-xs font-mono uppercase">Assigned user</TableHead>
                      <TableHead className="w-[112px] text-xs font-mono uppercase">Suppliers</TableHead>
                      <TableHead className="w-[76px] text-xs font-mono uppercase">Source</TableHead>
                      <TableHead className="w-[108px] text-xs font-mono uppercase">Status</TableHead>
                      <TableHead className="w-[68px] text-xs font-mono uppercase">Repeat</TableHead>
                      <TableHead className="w-[148px] text-xs font-mono uppercase">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leadsLoading ? (
                      <TableRow>
                        <TableCell colSpan={isAgent ? 11 : 12} className="text-center py-8 text-neutral-500">
                          <span className="inline-flex items-center gap-2">
                            <CircleNotch size={14} className="animate-spin" />
                            Loading leads…
                          </span>
                        </TableCell>
                      </TableRow>
                    ) : filteredLeads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isAgent ? 11 : 12} className="text-center py-8 text-neutral-400">
                          {leadSearch ? `No leads match “${leadSearch}”.` : "No leads found"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLeads.map((lead, i) => (
                        <TableRow key={lead.id} className="border-neutral-100" data-testid={`lead-row-${lead.id}`}>
                          {!isAgent && (
                            <TableCell className="align-middle">
                              <Checkbox
                                checked={selectedLeadIds.has(lead.id)}
                                onCheckedChange={(checked) => toggleLeadSelected(lead.id, checked)}
                                aria-label={`Select lead ${lead.buyer_phone || lead.id}`}
                              />
                            </TableCell>
                          )}
                          <TableCell className="text-xs font-mono text-neutral-500 align-middle">
                            {(Math.max(1, leadPage || 1) - 1) * (leadRowsPerPage || 0) + i + 1}
                          </TableCell>
                          <TableCell className="text-sm align-middle whitespace-nowrap tabular-nums">{lead.buyer_phone}</TableCell>
                          <TableCell className="text-sm align-middle whitespace-nowrap">
                            <Badge
                              className={`rounded-sm text-xs border capitalize ${
                                (lead.requirement_type || "rent") === "sell"
                                  ? "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300"
                                  : (lead.requirement_type || "rent") === "buy"
                                    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                                    : "bg-sky-100 text-sky-800 border-sky-300"
                              }`}
                            >
                              {lead.requirement_type || "rent"}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[140px] text-sm align-middle">
                            <div className="truncate" title={lead.city || ""}>
                              {lead.city}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm align-middle whitespace-nowrap tabular-nums text-center">{lead.items?.length || 0}</TableCell>
                          <TableCell className="max-w-[120px] text-sm align-middle" data-testid={`lead-staff-${lead.id}`}>
                            <Select
                              value={lead.assigned_agent_id || UNASSIGNED_AGENT}
                              onValueChange={(v) => onReassignLeadAgent(lead.id, v)}
                            >
                              <SelectTrigger className="h-7 w-full max-w-[120px] rounded-sm border-neutral-300 px-2 text-[11px] [&>span]:truncate [&>span]:text-left">
                                <SelectValue placeholder="Unassigned" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={UNASSIGNED_AGENT}>Unassigned</SelectItem>
                                {agentSelectOptions(lead).map((a) => (
                                  <SelectItem key={a.id} value={a.id}>
                                    {(a.name || a.email || a.id).slice(0, 36)}
                                    {a.is_active === false ? " (inactive)" : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="max-w-[112px] text-sm align-middle" data-testid={`lead-assigned-${lead.id}`}>
                            {(lead.assignments || []).length === 0 ? (
                              <span className="text-neutral-400 text-xs">—</span>
                            ) : (
                              <div className="flex flex-col gap-1">
                                {lead.assignments.map((a) => (
                                  <Badge
                                    key={a.id}
                                    variant="outline"
                                    className="inline-block max-w-[104px] truncate rounded-sm border-orange-200 bg-orange-50 text-[10px] font-normal text-orange-900"
                                    title={a.supplier_name || a.supplier_id || "Supplier"}
                                  >
                                    {a.supplier_name || a.supplier_id?.slice(0, 8) || "Supplier"}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="align-middle whitespace-nowrap">
                            <Badge variant="secondary" className="rounded-sm text-[11px] whitespace-nowrap capitalize">{lead.source}</Badge>
                          </TableCell>
                          <TableCell className="max-w-[108px] align-middle whitespace-nowrap" data-testid={`lead-status-${lead.id}`}>
                            <Select
                              value={lead.status}
                              onValueChange={(v) => updateLeadStatus(lead.id, v)}
                              disabled={leadStatusSavingId === lead.id}
                            >
                              <SelectTrigger
                                className={`h-7 w-full max-w-[108px] rounded-sm border px-2 text-[11px] font-medium [&>span]:truncate [&>span]:text-left [&>svg]:h-3.5 [&>svg]:w-3.5 ${
                                  STATUS_COLORS[lead.status] ||
                                  "bg-neutral-100 text-neutral-800 border-neutral-300"
                                }`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(LEAD_STATUSES).map(([key, label]) => (
                                  <SelectItem key={key} value={key} className="text-xs">
                                    <span
                                      className={`mr-2 inline-block h-2 w-2 rounded-full border ${
                                        STATUS_COLORS[key] || "bg-neutral-200 border-neutral-300"
                                      }`}
                                    />
                                    {label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="align-middle whitespace-nowrap">
                            {lead.is_repeat_buyer && (
                              <Badge className="rounded-sm text-xs bg-blue-100 text-blue-800 border border-blue-300 whitespace-nowrap">
                                Repeat
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="align-middle">
                            <div className="flex flex-nowrap gap-1">
                              <Button
                                type="button"
                                data-testid={`edit-lead-${lead.id}`}
                                size="sm"
                                variant="outline"
                                onClick={() => openLeadEdit(lead)}
                                aria-label="Edit lead"
                                title="Edit"
                                className="rounded-sm h-7 w-8 px-0 border-neutral-300 text-neutral-800 hover:bg-neutral-100 shrink-0"
                              >
                                <PencilSimple size={14} />
                              </Button>
                              {lead.status === "verified" && (
                                <>
                                  <Button
                                    type="button"
                                    data-testid={`check-status-btn-${lead.id}`}
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openPipelineStatus(lead)}
                                    aria-label="Check supplier status"
                                    title="Check status"
                                    className="rounded-sm h-7 w-8 px-0 border-neutral-300 hover:bg-neutral-100 text-neutral-800 shrink-0"
                                  >
                                    <Pulse size={14} />
                                  </Button>
                                  <Button
                                    data-testid={`assign-btn-${lead.id}`}
                                    size="sm"
                                    onClick={() => openAssign(lead)}
                                    disabled={!leadHasProducts(lead)}
                                    title={!leadHasProducts(lead) ? "Add at least one product line (Edit lead) before assigning" : undefined}
                                    aria-label={(lead.assignments || []).length ? "Reassign to suppliers" : "Assign to suppliers"}
                                    className="rounded-sm h-7 w-8 px-0 bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 disabled:pointer-events-auto shrink-0"
                                  >
                                    <PaperPlaneTilt size={14} />
                                  </Button>
                                </>
                              )}
                              {!isAgent && (
                                <Button
                                  type="button"
                                  data-testid={`delete-lead-${lead.id}`}
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    setLeadDeleteTarget({
                                      id: lead.id,
                                      label: lead.buyer_phone || lead.id.slice(0, 8),
                                    })
                                  }
                                  aria-label="Delete lead"
                                  title="Delete"
                                  className="rounded-sm h-7 w-8 px-0 border-red-300 text-red-700 hover:bg-red-50 shrink-0"
                                >
                                  <Trash size={14} />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-3 py-2 border-t border-neutral-200 bg-white">
                <div className="text-xs text-neutral-600 tabular-nums">
                  {(() => {
                    const total = Math.max(0, leadsTotal || 0);
                    const per = Math.max(1, leadRowsPerPage || 1);
                    const page = Math.max(1, leadPage || 1);
                    const from = total === 0 ? 0 : (page - 1) * per + 1;
                    const to = Math.min(total, page * per);
                    return `Showing ${from}-${to} of ${total}`;
                  })()}
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-600">Rows per page</span>
                    <Select
                      value={String(leadRowsPerPage)}
                      onValueChange={(v) => {
                        const next = Math.max(1, Number(v) || 25);
                        setLeadRowsPerPage(next);
                        setLeadPage(1);
                        setSelectedLeadIds(new Set());
                      }}
                    >
                      <SelectTrigger className="h-8 w-[92px] rounded-sm border-neutral-300 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[10, 25, 50, 100].map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {n}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {(() => {
                    const total = Math.max(0, leadsTotal || 0);
                    const per = Math.max(1, leadRowsPerPage || 1);
                    const pageCount = Math.max(1, Math.ceil(total / per));
                    const page = Math.min(Math.max(1, leadPage || 1), pageCount);
                    const canPrev = page > 1 && !leadsLoading;
                    const canNext = page < pageCount && !leadsLoading;
                    return (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-neutral-600 tabular-nums">
                          Page {page} / {pageCount}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={!canPrev}
                          onClick={() => {
                            setLeadPage((p) => Math.max(1, (p || 1) - 1));
                            setSelectedLeadIds(new Set());
                          }}
                          className="rounded-sm h-8 px-2 text-xs border-neutral-300"
                        >
                          Prev
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={!canNext}
                          onClick={() => {
                            setLeadPage((p) => (p || 1) + 1);
                            setSelectedLeadIds(new Set());
                          }}
                          className="rounded-sm h-8 px-2 text-xs border-neutral-300"
                        >
                          Next
                        </Button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* SUPPLIERS TAB (admin only) */}
          {!isAgent && (
            <TabsContent value="suppliers" data-testid="suppliers-tab">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div>
                  <h3 className="font-bold">Suppliers</h3>
                  <p className="text-sm text-neutral-500 mt-1">
                    Active suppliers appear in the public directory and assignment pickers.
                    {showInactiveSuppliers
                      ? " Inactive accounts are shown below — use Edit to turn Active back on."
                      : " Turn on “Show inactive” to find and reactivate deactivated accounts."}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 shrink-0">
                  <div className="relative w-full sm:w-72">
                    <MagnifyingGlass
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
                      aria-hidden
                    />
                    <Input
                      data-testid="supplier-tab-search"
                      className="rounded-sm border-neutral-300 pl-9 pr-9 bg-white"
                      placeholder="Search by name, company, or city…"
                      value={supplierSearchInput}
                      onChange={(e) => setSupplierSearchInput(e.target.value)}
                      aria-label="Search suppliers by name, company, or city"
                    />
                    {supplierSearchInput ? (
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-sm text-neutral-500 hover:bg-neutral-100"
                        onClick={() => {
                          setSupplierSearchInput("");
                          setSupplierSearch("");
                          setSupplierPage(1);
                        }}
                        aria-label="Clear supplier search"
                      >
                        <X size={16} />
                      </button>
                    ) : null}
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-sm border border-neutral-200 bg-white px-3 py-2.5">
                    <Label htmlFor="show-inactive-suppliers" className="text-sm font-medium cursor-pointer">
                      Show inactive suppliers
                    </Label>
                    <Switch
                      id="show-inactive-suppliers"
                      data-testid="show-inactive-suppliers"
                      checked={showInactiveSuppliers}
                      onCheckedChange={onShowInactiveSuppliersChange}
                    />
                  </div>
                </div>
              </div>
              <Card className="rounded-sm border-neutral-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-neutral-200 bg-neutral-50">
                        <TableHead className="text-xs font-mono uppercase w-12">#</TableHead>
                        <TableHead className="text-xs font-mono uppercase">Name</TableHead>
                        <TableHead className="text-xs font-mono uppercase">Company</TableHead>
                        <TableHead className="text-xs font-mono uppercase">Cities</TableHead>
                        <TableHead className="text-xs font-mono uppercase">Products</TableHead>
                        <TableHead className="text-xs font-mono uppercase">Score</TableHead>
                        {SUPPLIER_TABLE_PIPELINE_STAGES.map(({ key, label }) => (
                          <TableHead key={key} className="text-xs font-mono uppercase text-center min-w-[4.5rem]">
                            {label}
                          </TableHead>
                        ))}
                        <TableHead className="text-xs font-mono uppercase">Total Leads</TableHead>
                        <TableHead className="text-xs font-mono uppercase">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {suppliersLoading && suppliers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6 + SUPPLIER_TABLE_PIPELINE_STAGES.length + 2} className="text-center py-8 text-neutral-400">
                            Loading suppliers…
                          </TableCell>
                        </TableRow>
                      ) : suppliers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6 + SUPPLIER_TABLE_PIPELINE_STAGES.length + 2} className="text-center py-8 text-neutral-400">
                            {supplierSearch
                              ? `No suppliers match “${supplierSearch}”.`
                              : showInactiveSuppliers
                                ? "No suppliers"
                                : "No active suppliers"}
                          </TableCell>
                        </TableRow>
                      ) : (
                        suppliers.map((s, i) => (
                          <TableRow
                            key={s.id}
                            className={`border-neutral-100 ${s.is_active === false ? "bg-neutral-50/80 text-neutral-600" : ""}`}
                            data-testid={`supplier-row-${s.id}`}
                          >
                            <TableCell className="text-xs font-mono text-neutral-500">
                              {(Math.max(1, supplierPage || 1) - 1) * (supplierRowsPerPage || 0) + i + 1}
                            </TableCell>
                            <TableCell className="font-medium text-sm">
                              <span className="align-middle">{s.name}</span>
                              {s.is_active === false && (
                                <Badge className="rounded-sm text-xs ml-2 align-middle bg-neutral-200 text-neutral-700 border border-neutral-400">
                                  Inactive
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">{s.company_name}</TableCell>
                            <TableCell className="text-sm text-neutral-600 max-w-[14rem]">
                              <span className="block truncate" title={(s.cities_served || []).join(", ")}>
                                {(s.cities_served || []).length > 0 ? (s.cities_served || []).join(", ") : "—"}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-neutral-600">{parseProductsSupplied(s.products_supplied).length || "—"}</TableCell>
                            <TableCell className="font-mono text-sm">{s.performance_score}%</TableCell>
                            {SUPPLIER_TABLE_PIPELINE_STAGES.map(({ key }) => (
                              <TableCell key={key} className="font-mono text-sm text-center tabular-nums">
                                {s.pipeline_counts?.[key] ?? 0}
                              </TableCell>
                            ))}
                            <TableCell className="font-mono text-sm">{s.total_leads}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  data-testid={`supplier-edit-${s.id}`}
                                  onClick={() => openSupplierEdit(s)}
                                  className="rounded-sm text-xs h-7 border-neutral-300"
                                >
                                  <PencilSimple size={12} className="mr-0.5" /> Edit
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  data-testid={`supplier-delete-${s.id}`}
                                  disabled={s.is_active === false}
                                  title={s.is_active === false ? "Already deactivated" : "Remove from public directory"}
                                  onClick={() => setSupplierDeleteTarget({ id: s.id, name: s.name || s.company_name || "Supplier" })}
                                  className="rounded-sm text-xs h-7 border-red-300 text-red-700 hover:bg-red-50"
                                >
                                  <Trash size={12} className="mr-0.5" /> Delete
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-3 py-2 border-t border-neutral-200 bg-white">
                  <div className="text-xs text-neutral-600 tabular-nums">
                    {(() => {
                      const total = Math.max(0, suppliersTotal || 0);
                      const per = Math.max(1, supplierRowsPerPage || 1);
                      const page = Math.max(1, supplierPage || 1);
                      const from = total === 0 ? 0 : (page - 1) * per + 1;
                      const to = Math.min(total, page * per);
                      return `Showing ${from}-${to} of ${total}`;
                    })()}
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-neutral-600">Rows per page</span>
                      <Select
                        value={String(supplierRowsPerPage)}
                        onValueChange={(v) => {
                          const next = Math.max(1, Number(v) || 25);
                          setSupplierRowsPerPage(next);
                          setSupplierPage(1);
                        }}
                      >
                        <SelectTrigger className="h-8 w-[92px] rounded-sm border-neutral-300 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[10, 25, 50, 100].map((n) => (
                            <SelectItem key={n} value={String(n)}>
                              {n}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {(() => {
                      const total = Math.max(0, suppliersTotal || 0);
                      const per = Math.max(1, supplierRowsPerPage || 1);
                      const pageCount = Math.max(1, Math.ceil(total / per));
                      const page = Math.min(Math.max(1, supplierPage || 1), pageCount);
                      const canPrev = page > 1 && !suppliersLoading;
                      const canNext = page < pageCount && !suppliersLoading;
                      return (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-neutral-600 tabular-nums">
                            Page {page} / {pageCount}
                          </span>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={!canPrev}
                            onClick={() => setSupplierPage((p) => Math.max(1, (p || 1) - 1))}
                            className="rounded-sm h-8 px-2 text-xs border-neutral-300"
                          >
                            Prev
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={!canNext}
                            onClick={() => setSupplierPage((p) => (p || 1) + 1)}
                            className="rounded-sm h-8 px-2 text-xs border-neutral-300"
                          >
                            Next
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </Card>
            </TabsContent>
          )}

          {/* CATEGORIES TAB */}
          <TabsContent value="categories" data-testid="categories-tab">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-4">
              <div>
                <h3 className="font-bold">Categories</h3>
                <p className="text-sm text-neutral-500 mt-1">
                  Parent groups and subcategories are listed separately. Use <span className="font-medium text-neutral-700">Parent categories</span> to manage parent groups;{" "}
                  <span className="font-medium text-neutral-700">Add subcategory</span> maps a row to one of those parents in the form.
                </p>
              </div>
              {!isAgent && (
                <div className="flex flex-wrap gap-2 justify-end shrink-0">
                  <Button
                    data-testid="add-parent-category-btn"
                    type="button"
                    variant="outline"
                    onClick={() => setParentCategoriesModalOpen(true)}
                    className="rounded-sm border-neutral-300"
                  >
                    <Folders size={14} className="mr-1" /> Parent categories
                  </Button>
                  <Button
                    data-testid="add-subcategory-btn"
                    type="button"
                    onClick={openCategoryCreateSub}
                    className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    <Plus size={14} className="mr-1" /> Add subcategory
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-8">
              <section data-testid="categories-parent-section">
                <div className="mb-2">
                  <h4 className="font-bold text-neutral-900">Parent categories</h4>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Top-level groups used for nesting. Open “Parent categories” to add or review this list.
                  </p>
                </div>
                <Card className="rounded-sm border-neutral-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-neutral-200 bg-neutral-50">
                          <TableHead className="text-xs font-mono uppercase w-12">#</TableHead>
                          <TableHead className="text-xs font-mono uppercase">Name</TableHead>
                          <TableHead className="text-xs font-mono uppercase">Slug</TableHead>
                          <TableHead className="text-xs font-mono uppercase">Type</TableHead>
                          <TableHead className="text-xs font-mono uppercase">Tier</TableHead>
                          <TableHead className="text-xs font-mono uppercase">Active</TableHead>
                          <TableHead className="text-xs font-mono uppercase">Sort</TableHead>
                          {!isAgent && <TableHead className="text-xs font-mono uppercase min-w-[220px]">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {parentGroupsTableRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={categoryTableEmptyColSpan(false)} className="text-center py-8 text-neutral-400 text-sm">
                              No parent groups yet. Use “Parent categories” → Add parent category.
                            </TableCell>
                          </TableRow>
                        ) : (
                          parentGroupsTableRows.map((c, i) => renderCategoryTableRow(c, false, i + 1))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              </section>

              <section data-testid="categories-sub-section">
                <div className="mb-2">
                  <h4 className="font-bold text-neutral-900">Subcategories</h4>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Items nested under a parent group. Add or edit maps the parent in the form (“Under parent”).
                  </p>
                </div>
                <Card className="rounded-sm border-neutral-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-neutral-200 bg-neutral-50">
                          <TableHead className="text-xs font-mono uppercase w-12">#</TableHead>
                          <TableHead className="text-xs font-mono uppercase">Name</TableHead>
                          <TableHead className="text-xs font-mono uppercase">Parent</TableHead>
                          <TableHead className="text-xs font-mono uppercase">Slug</TableHead>
                          <TableHead className="text-xs font-mono uppercase">Type</TableHead>
                          <TableHead className="text-xs font-mono uppercase">Tier</TableHead>
                          <TableHead className="text-xs font-mono uppercase">Active</TableHead>
                          <TableHead className="text-xs font-mono uppercase">Sort</TableHead>
                          {!isAgent && <TableHead className="text-xs font-mono uppercase min-w-[220px]">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subcategoriesTableRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={categoryTableEmptyColSpan(true)} className="text-center py-8 text-neutral-400 text-sm">
                              No subcategories yet. Use “Add subcategory” and choose the parent group.
                            </TableCell>
                          </TableRow>
                        ) : (
                          subcategoriesTableRows.map((c, i) => renderCategoryTableRow(c, true, i + 1))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              </section>

              {otherTopLevelTableRows.length > 0 ? (
                <section data-testid="categories-other-section">
                  <div className="mb-2">
                    <h4 className="font-bold text-neutral-900">Other top-level categories</h4>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      Standalone categories (not parent groups). Edit and enable “Parent group” if one should hold subcategories.
                    </p>
                  </div>
                  <Card className="rounded-sm border-neutral-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-neutral-200 bg-neutral-50">
                            <TableHead className="text-xs font-mono uppercase w-12">#</TableHead>
                            <TableHead className="text-xs font-mono uppercase">Name</TableHead>
                            <TableHead className="text-xs font-mono uppercase">Slug</TableHead>
                            <TableHead className="text-xs font-mono uppercase">Type</TableHead>
                            <TableHead className="text-xs font-mono uppercase">Tier</TableHead>
                            <TableHead className="text-xs font-mono uppercase">Active</TableHead>
                            <TableHead className="text-xs font-mono uppercase">Sort</TableHead>
                            {!isAgent && <TableHead className="text-xs font-mono uppercase min-w-[220px]">Actions</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {otherTopLevelTableRows.map((c, i) => renderCategoryTableRow(c, false, i + 1))}
                        </TableBody>
                      </Table>
                    </div>
                  </Card>
                </section>
              ) : null}
            </div>
          </TabsContent>

          {/* SUPPLIER CATALOG REQUESTS */}
          <TabsContent value="product-requests" data-testid="product-requests-tab">
            <div className="flex flex-wrap gap-2 mb-4">
              {["pending", "approved", "rejected", "all"].map((s) => (
                <Button
                  key={s}
                  data-testid={`pr-filter-${s}`}
                  variant={prFilter === s ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPrFilter(s)}
                  className={`rounded-sm text-xs ${prFilter === s ? "bg-neutral-900 text-white" : "border-neutral-300"}`}
                >
                  {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                  {s !== "all" && ` (${productRequests.filter((r) => r.status === s).length})`}
                  {s === "all" && ` (${productRequests.length})`}
                </Button>
              ))}
            </div>
            <Card className="rounded-sm border-neutral-200 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-neutral-200 bg-neutral-50">
                      <TableHead className="text-xs font-mono uppercase w-12">#</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Supplier</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Product</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Image</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Category</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Status</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Submitted</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProductRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-neutral-400">
                          No requests in this filter
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredProductRequests.map((r, i) => (
                        <TableRow key={r.id} className="border-neutral-100" data-testid={`pr-row-${r.id}`}>
                          <TableCell className="text-xs font-mono text-neutral-500">{i + 1}</TableCell>
                          <TableCell className="text-sm">
                            <span className="font-medium">{r.supplier_name || "—"}</span>
                          </TableCell>
                          <TableCell className="text-sm max-w-[200px]">
                            <span className="font-medium">{r.name}</span>
                            {r.description ? (
                              <p className="text-xs text-neutral-500 mt-0.5 line-clamp-2">{r.description}</p>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-sm">
                            {r.image_url ? (
                              <div className="w-12 h-12 rounded-sm overflow-hidden border border-neutral-200 bg-neutral-100">
                                <img
                                  src={publicAssetUrl(r.image_url)}
                                  alt={r.name || "Requested product"}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-neutral-400">—</span>
                            )}
                          </TableCell>
                          <TableCell><Badge variant="secondary" className="rounded-sm text-xs">{r.category}</Badge></TableCell>
                          <TableCell>
                            <Badge
                              className={`rounded-sm text-xs border ${
                                r.status === "pending"
                                  ? "bg-amber-100 text-amber-900 border-amber-300"
                                  : r.status === "approved"
                                    ? "bg-green-100 text-green-900 border-green-300"
                                    : "bg-red-100 text-red-900 border-red-300"
                              }`}
                            >
                              {r.status}
                            </Badge>
                            {r.status === "rejected" && r.review_note ? (
                              <p className="text-[10px] text-neutral-500 mt-1 max-w-[140px]">{r.review_note}</p>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-xs font-mono text-neutral-600 whitespace-nowrap">
                            {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                          </TableCell>
                          <TableCell>
                            {r.status === "pending" ? (
                              <div className="flex flex-wrap gap-1">
                                <Button
                                  data-testid={`pr-approve-${r.id}`}
                                  size="sm"
                                  className="rounded-sm text-xs h-7 bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => approveProductRequest(r.id)}
                                >
                                  <CheckCircle size={12} className="mr-0.5" /> Approve
                                </Button>
                                <Button
                                  data-testid={`pr-reject-${r.id}`}
                                  size="sm"
                                  variant="outline"
                                  className="rounded-sm text-xs h-7 border-red-300 text-red-700 hover:bg-red-50"
                                  onClick={() => openRejectProductRequest(r.id)}
                                >
                                  <XCircle size={12} className="mr-0.5" /> Reject
                                </Button>
                              </div>
                            ) : r.product_id ? (
                              <span className="text-xs font-mono text-neutral-400">{r.product_id.slice(0, 8)}…</span>
                            ) : (
                              <span className="text-xs text-neutral-400">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* PRODUCTS TAB */}
          <TabsContent value="products" data-testid="products-tab">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold">Product Catalog</h3>
              <Button data-testid="add-product-btn" size="sm" onClick={() => {
                const first = productSubcategoryOptions[0] || "General";
                setNewProduct(prev => ({ ...prev, category: productSubcategoryOptions.includes(prev.category) ? prev.category : first }));
                setProductDialog(true);
              }} className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white">
                <Plus size={14} className="mr-1" /> Add Product
              </Button>
            </div>
            <Card className="rounded-sm border-neutral-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-200 bg-neutral-50">
                    <TableHead className="text-xs font-mono uppercase w-12">#</TableHead>
                    <TableHead className="text-xs font-mono uppercase w-12">Image</TableHead>
                    <TableHead className="text-xs font-mono uppercase">Name</TableHead>
                    <TableHead className="text-xs font-mono uppercase">Category</TableHead>
                    {!isAgent && <TableHead className="text-xs font-mono uppercase">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.length === 0 ? (
                    <TableRow><TableCell colSpan={isAgent ? 4 : 5} className="text-center py-8 text-neutral-400">No products yet</TableCell></TableRow>
                  ) : (
                    products.map((p, i) => (
                      <TableRow key={p.id} className="border-neutral-100" data-testid={`product-row-${p.id}`}>
                        <TableCell className="text-xs font-mono text-neutral-500">{i + 1}</TableCell>
                        <TableCell><div className="w-10 h-10 rounded-sm overflow-hidden bg-neutral-100">{p.image_url ? <img src={publicAssetUrl(p.image_url)} alt="" className="w-full h-full object-cover" /> : <Package size={16} className="text-neutral-300 m-2.5" />}</div></TableCell>
                        <TableCell className="font-medium text-sm">{p.name}</TableCell>
                        <TableCell><Badge variant="secondary" className="rounded-sm text-xs">{p.category}</Badge></TableCell>
                        {!isAgent && (
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              <Button
                                type="button"
                                data-testid={`edit-product-${p.id}`}
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setProductEdit({
                                    id: p.id,
                                    name: p.name || "",
                                    category: p.category || "General",
                                    image_url: p.image_url || "",
                                    sizeList: parseProductSizes(p.sizes),
                                  });
                                  setProductEditImageFile(null);
                                }}
                                className="rounded-sm text-xs h-7 border-neutral-300"
                              >
                                <PencilSimple size={12} />
                              </Button>
                              <Button data-testid={`delete-product-${p.id}`} size="sm" variant="outline" onClick={() => deleteProduct(p.id)} className="rounded-sm text-xs h-7 border-red-300 text-red-700 hover:bg-red-50">
                                <Trash size={12} />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="blog" data-testid="blog-tab">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <h3 className="font-bold">Blog posts</h3>
              <Button
                type="button"
                data-testid="add-blog-post-btn"
                size="sm"
                onClick={openBlogCreate}
                className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white"
              >
                <Plus size={14} className="mr-1" /> Add post
              </Button>
            </div>
            <p className="text-sm text-neutral-600 mb-4">
              Published posts appear on the public{" "}
              <a href="/blog" target="_blank" rel="noreferrer" className="text-[#FF7A00] font-medium hover:underline">
                /blog
              </a>{" "}
              page. Drafts are saved but not visible to visitors.
            </p>
            <Card className="rounded-sm border-neutral-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-200 bg-neutral-50">
                    <TableHead className="text-xs font-mono uppercase w-12">#</TableHead>
                    <TableHead className="text-xs font-mono uppercase">Title</TableHead>
                    <TableHead className="text-xs font-mono uppercase">Slug</TableHead>
                    <TableHead className="text-xs font-mono uppercase">Status</TableHead>
                    <TableHead className="text-xs font-mono uppercase hidden sm:table-cell">Updated</TableHead>
                    <TableHead className="text-xs font-mono uppercase w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blogPosts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-neutral-400">
                        No blog posts yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    blogPosts.map((b, i) => (
                      <TableRow key={b.id} className="border-neutral-100" data-testid={`blog-row-${b.id}`}>
                        <TableCell className="text-xs font-mono text-neutral-500">{i + 1}</TableCell>
                        <TableCell className="font-medium text-sm max-w-[200px]">
                          <span className="line-clamp-2">{b.title}</span>
                        </TableCell>
                        <TableCell className="text-xs text-neutral-600 font-mono max-w-[140px] truncate">{b.slug}</TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={`rounded-sm text-xs ${b.published ? "bg-green-100 text-green-900 border-green-300" : "bg-neutral-100 text-neutral-700 border-neutral-300"}`}
                          >
                            {b.published ? "Published" : "Draft"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-neutral-500 hidden sm:table-cell whitespace-nowrap">
                          {b.updated_at ? new Date(b.updated_at).toLocaleString() : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              data-testid={`edit-blog-${b.id}`}
                              onClick={() => openBlogEdit(b)}
                              className="rounded-sm text-xs h-7 border-neutral-300"
                            >
                              <PencilSimple size={12} />
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              data-testid={`delete-blog-${b.id}`}
                              onClick={() => deleteBlogPost(b.id)}
                              className="rounded-sm text-xs h-7 border-red-300 text-red-700 hover:bg-red-50"
                            >
                              <Trash size={12} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="old-material" data-testid="old-material-tab">
            <AdminOldMaterialTab isAgent={isAgent} />
          </TabsContent>

        </Tabs>
      </div>

      <Dialog open={blogDialogOpen} onOpenChange={setBlogDialogOpen}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="blog-post-dialog">
          <DialogHeader>
            <DialogTitle>{blogEditId ? "Edit blog post" : "New blog post"}</DialogTitle>
            <DialogDescription>
              Leave URL slug empty to generate from the title. Turn on Published to show it on the public blog.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">Title</Label>
              <Input
                className="rounded-sm border-neutral-300 mt-1"
                data-testid="blog-form-title"
                value={blogForm.title}
                onChange={(e) => setBlogForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="Post title"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">URL slug</Label>
              <Input
                className="rounded-sm border-neutral-300 mt-1 font-mono text-sm"
                data-testid="blog-form-slug"
                value={blogForm.slug}
                onChange={(e) => setBlogForm((p) => ({ ...p, slug: e.target.value }))}
                placeholder="auto-from-title"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">Excerpt (optional)</Label>
              <Textarea
                className="rounded-sm border-neutral-300 mt-1 min-h-[72px]"
                data-testid="blog-form-excerpt"
                value={blogForm.excerpt}
                onChange={(e) => setBlogForm((p) => ({ ...p, excerpt: e.target.value }))}
                placeholder="Short summary for the blog index"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">Body</Label>
              <Textarea
                className="rounded-sm border-neutral-300 mt-1 min-h-[200px] font-sans text-sm"
                data-testid="blog-form-body"
                value={blogForm.body}
                onChange={(e) => setBlogForm((p) => ({ ...p, body: e.target.value }))}
                placeholder="Article content"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="blog-published"
                checked={blogForm.published}
                onCheckedChange={(v) => setBlogForm((p) => ({ ...p, published: !!v }))}
              />
              <Label htmlFor="blog-published" className="text-sm font-medium cursor-pointer">
                Published (visible on /blog)
              </Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" className="rounded-sm" onClick={() => setBlogDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="blog-form-save"
                disabled={blogSaving}
                onClick={saveBlogPost}
              >
                {blogSaving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit lead (site / RFQ only — not buyer personal data) */}
      <Dialog open={!!leadEdit} onOpenChange={(open) => !open && setLeadEdit(null)}>
        <DialogContent
          className="w-full max-w-[calc(100vw-1.5rem)] sm:max-w-5xl rounded-sm max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0"
          data-testid="edit-lead-dialog"
        >
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-6 space-y-4 sm:space-y-6">
            <DialogHeader className="space-y-2 text-left pr-10 sm:pr-12">
              <DialogTitle className="leading-snug break-words">Edit lead requirements</DialogTitle>
              <DialogDescription className="break-words text-xs sm:text-sm">
                City, schedule, and product lines only. Buyer phone and name are not changed here.
              </DialogDescription>
            </DialogHeader>
            {leadEdit && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="relative z-20">
                  <Label className="text-xs font-mono uppercase text-neutral-500">City</Label>
                  <CityGoogleAutocomplete
                    value={leadEdit.city}
                    onChange={(v) => setLeadEdit({ ...leadEdit, city: v })}
                    onSelect={(v) => setLeadEdit({ ...leadEdit, city: v })}
                    placeholder="Search city…"
                    predictionsPlacement="auto"
                    className={ADD_LEAD_CITY_CLASS}
                    containerTestId="edit-lead-city"
                    inputTestId="edit-lead-city-search"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-500">Start date</Label>
                  <Input
                    type="date"
                    className="rounded-sm border-neutral-300 mt-1"
                    data-testid="edit-lead-start"
                    value={leadEdit.start_date}
                    onChange={(e) => setLeadEdit({ ...leadEdit, start_date: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Duration</Label>
                <Select
                  value={leadEdit.durationMode}
                  onValueChange={(v) => setLeadEdit({ ...leadEdit, durationMode: v })}
                >
                  <SelectTrigger className="rounded-sm border-neutral-300 mt-1" data-testid="edit-lead-duration-preset">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                    <SelectItem value={CUSTOM_DURATION}>Custom…</SelectItem>
                  </SelectContent>
                </Select>
                {leadEdit.durationMode === CUSTOM_DURATION && (
                  <Input
                    className="rounded-sm border-neutral-300 mt-2"
                    data-testid="edit-lead-duration-custom"
                    placeholder="e.g. 8 weeks"
                    value={leadEdit.customDuration}
                    onChange={(e) => setLeadEdit({ ...leadEdit, customDuration: e.target.value })}
                  />
                )}
              </div>
              <div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
                  <Label className="text-xs font-mono uppercase text-neutral-500">Product lines</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-sm text-xs h-7 w-full sm:w-auto shrink-0"
                    data-testid="edit-lead-add-line"
                    onClick={() =>
                      setLeadEdit((prev) => ({
                        ...prev,
                        lines: [...prev.lines, emptyLeadEditLine()],
                      }))
                    }
                  >
                    <Plus size={12} className="mr-0.5" /> Add line
                  </Button>
                </div>
                <div className="md:hidden space-y-3">
                  {leadEdit.lines.map((line, idx) => (
                    <div
                      key={line.key}
                      className="rounded-sm border border-neutral-200 bg-white p-3 space-y-3 shadow-sm"
                    >
                      <div className="flex items-center justify-between gap-2 border-b border-neutral-100 pb-2">
                        <span className="text-xs font-mono uppercase text-neutral-500">Line {idx + 1}</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-red-600 h-9 w-9 p-0 shrink-0"
                          data-testid={`edit-lead-remove-line-m-${idx}`}
                          onClick={() =>
                            setLeadEdit((prev) => ({
                              ...prev,
                              lines: prev.lines.filter((ln) => ln.key !== line.key),
                            }))
                          }
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-mono uppercase text-neutral-500">From catalog</Label>
                        <Select
                          value={line.catalogId}
                            onValueChange={(v) => {
                              setLeadEdit((prev) => ({
                                ...prev,
                                lines: prev.lines.map((ln) =>
                                  ln.key === line.key ? mapLeadLineCatalogSelection(ln, v, products) : ln
                                ),
                              }));
                            }}
                        >
                          <SelectTrigger className="rounded-sm border-neutral-300 h-9 text-xs w-full max-w-full" data-testid={`edit-lead-catalog-${idx}`}>
                            <SelectValue placeholder="Catalog or custom" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={CUSTOM_DURATION}>Custom (type name)</SelectItem>
                            {products.map((pr) => (
                              <SelectItem key={pr.id} value={pr.id}>{pr.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {line.catalogId === CUSTOM_DURATION ? (
                        <div className="space-y-1">
                          <Label className="text-[10px] font-mono uppercase text-neutral-500">Custom name</Label>
                          <Input
                            className="rounded-sm border-neutral-300 h-9 text-sm w-full"
                            data-testid={`edit-lead-name-${idx}`}
                            placeholder="Describe the product"
                            value={line.product_name}
                            onChange={(e) =>
                              setLeadEdit((prev) => ({
                                ...prev,
                                lines: prev.lines.map((ln) =>
                                  ln.key === line.key ? { ...ln, product_name: e.target.value } : ln
                                ),
                              }))
                            }
                          />
                        </div>
                      ) : null}
                      <div className="space-y-1">
                        <Label className="text-[10px] font-mono uppercase text-neutral-500">Size</Label>
                        <LeadLineSizeSelect
                          line={line}
                          products={products}
                          value={line.size}
                          testId={`edit-lead-size-m-${idx}`}
                          onChange={(sz) =>
                            setLeadEdit((prev) => ({
                              ...prev,
                              lines: prev.lines.map((ln) =>
                                ln.key === line.key ? { ...ln, size: sz } : ln
                              ),
                            }))
                          }
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        <div className="space-y-1">
                          <Label className="text-[10px] font-mono uppercase text-neutral-500">Spec</Label>
                          <Select
                            value={line.spec_type}
                            onValueChange={(v) =>
                              setLeadEdit((prev) => ({
                                ...prev,
                                lines: prev.lines.map((ln) =>
                                  ln.key === line.key ? { ...ln, spec_type: v } : ln
                                ),
                              }))
                            }
                          >
                            <SelectTrigger className="rounded-sm border-neutral-300 h-9 text-xs w-full" data-testid={`edit-lead-spec-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="quantity">Quantity</SelectItem>
                              <SelectItem value="area">Area</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] font-mono uppercase text-neutral-500">
                            {line.spec_type === "quantity" ? "Quantity" : "Area"}
                          </Label>
                          {line.spec_type === "quantity" ? (
                            <Input
                              type="text"
                              inputMode="numeric"
                              className="rounded-sm border-neutral-300 h-9 w-full text-xs"
                              data-testid={`edit-lead-qty-${idx}`}
                              value={line.quantityStr}
                              onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, "");
                                setLeadEdit((prev) => ({
                                  ...prev,
                                  lines: prev.lines.map((ln) =>
                                    ln.key === line.key ? { ...ln, quantityStr: digits } : ln
                                  ),
                                }));
                              }}
                            />
                          ) : (
                            <Input
                              className="rounded-sm border-neutral-300 h-9 w-full text-xs"
                              data-testid={`edit-lead-area-${idx}`}
                              value={line.area_value}
                              onChange={(e) =>
                                setLeadEdit((prev) => ({
                                  ...prev,
                                  lines: prev.lines.map((ln) =>
                                    ln.key === line.key ? { ...ln, area_value: e.target.value } : ln
                                  ),
                                }))
                              }
                            />
                          )}
                        </div>
                      </div>
                      {line.spec_type === "area" ? (
                        <div className="space-y-1">
                          <Label className="text-[10px] font-mono uppercase text-neutral-500">Unit</Label>
                          <Select
                            value={line.area_unit}
                            onValueChange={(v) =>
                              setLeadEdit((prev) => ({
                                ...prev,
                                lines: prev.lines.map((ln) =>
                                  ln.key === line.key ? { ...ln, area_unit: v } : ln
                                ),
                              }))
                            }
                          >
                            <SelectTrigger className="rounded-sm border-neutral-300 h-9 text-xs w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sqft">sq ft</SelectItem>
                              <SelectItem value="sqm">m²</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
                <div className="hidden md:block overflow-x-auto border border-neutral-200 rounded-sm bg-neutral-50 max-w-full">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-neutral-200 bg-neutral-100 hover:bg-neutral-100">
                        <TableHead className="text-xs font-mono uppercase min-w-[200px] max-w-[280px]">From catalog</TableHead>
                        <TableHead className="text-xs font-mono uppercase min-w-[160px]">
                          Custom name
                          <span className="block font-sans font-normal normal-case text-[10px] text-neutral-500 font-mono">Only when not in catalog</span>
                        </TableHead>
                        <TableHead className="text-xs font-mono uppercase w-[120px]">Size</TableHead>
                        <TableHead className="text-xs font-mono uppercase w-[104px]">Spec</TableHead>
                        <TableHead className="text-xs font-mono uppercase w-[100px]">Qty / area</TableHead>
                        <TableHead className="text-xs font-mono uppercase w-[88px]">Unit</TableHead>
                        <TableHead className="w-12 p-2" aria-label="Remove row" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leadEdit.lines.map((line, idx) => (
                        <TableRow key={line.key} className="border-neutral-200 align-top">
                          <TableCell className="py-2 align-top">
                            <Select
                              value={line.catalogId}
                            onValueChange={(v) => {
                              setLeadEdit((prev) => ({
                                ...prev,
                                lines: prev.lines.map((ln) =>
                                  ln.key === line.key ? mapLeadLineCatalogSelection(ln, v, products) : ln
                                ),
                              }));
                            }}
                            >
                              <SelectTrigger className="rounded-sm border-neutral-300 h-9 text-xs max-w-[260px]" data-testid={`edit-lead-catalog-${idx}`}>
                                <SelectValue placeholder="Catalog or custom" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={CUSTOM_DURATION}>Custom (type name)</SelectItem>
                                {products.map((pr) => (
                                  <SelectItem key={pr.id} value={pr.id}>{pr.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="py-2 align-top">
                            {line.catalogId === CUSTOM_DURATION ? (
                              <Input
                                className="rounded-sm border-neutral-300 h-9 text-sm"
                                data-testid={`edit-lead-name-${idx}`}
                                placeholder="Describe the product"
                                value={line.product_name}
                                onChange={(e) =>
                                  setLeadEdit((prev) => ({
                                    ...prev,
                                    lines: prev.lines.map((ln) =>
                                      ln.key === line.key ? { ...ln, product_name: e.target.value } : ln
                                    ),
                                  }))
                                }
                              />
                            ) : (
                              <span className="text-xs text-neutral-400 flex items-center min-h-9 px-1" title="Name comes from catalog selection">
                                —
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="py-2 align-top">
                            <LeadLineSizeSelect
                              line={line}
                              products={products}
                              value={line.size}
                              testId={`edit-lead-size-${idx}`}
                              onChange={(sz) =>
                                setLeadEdit((prev) => ({
                                  ...prev,
                                  lines: prev.lines.map((ln) =>
                                    ln.key === line.key ? { ...ln, size: sz } : ln
                                  ),
                                }))
                              }
                            />
                          </TableCell>
                          <TableCell className="py-2 align-top">
                            <Select
                              value={line.spec_type}
                              onValueChange={(v) =>
                                setLeadEdit((prev) => ({
                                  ...prev,
                                  lines: prev.lines.map((ln) =>
                                    ln.key === line.key ? { ...ln, spec_type: v } : ln
                                  ),
                                }))
                              }
                            >
                              <SelectTrigger className="rounded-sm border-neutral-300 h-9 text-xs" data-testid={`edit-lead-spec-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="quantity">Quantity</SelectItem>
                                <SelectItem value="area">Area</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="py-2 align-top">
                            {line.spec_type === "quantity" ? (
                              <Input
                                type="text"
                                inputMode="numeric"
                                className="rounded-sm border-neutral-300 h-9 w-full min-w-[72px] text-xs"
                                data-testid={`edit-lead-qty-${idx}`}
                                value={line.quantityStr}
                                onChange={(e) => {
                                  const digits = e.target.value.replace(/\D/g, "");
                                  setLeadEdit((prev) => ({
                                    ...prev,
                                    lines: prev.lines.map((ln) =>
                                      ln.key === line.key ? { ...ln, quantityStr: digits } : ln
                                    ),
                                  }));
                                }}
                              />
                            ) : (
                              <Input
                                className="rounded-sm border-neutral-300 h-9 w-full min-w-[72px] text-xs"
                                data-testid={`edit-lead-area-${idx}`}
                                value={line.area_value}
                                onChange={(e) =>
                                  setLeadEdit((prev) => ({
                                    ...prev,
                                    lines: prev.lines.map((ln) =>
                                      ln.key === line.key ? { ...ln, area_value: e.target.value } : ln
                                    ),
                                  }))
                                }
                              />
                            )}
                          </TableCell>
                          <TableCell className="py-2 align-top">
                            {line.spec_type === "area" ? (
                              <Select
                                value={line.area_unit}
                                onValueChange={(v) =>
                                  setLeadEdit((prev) => ({
                                    ...prev,
                                    lines: prev.lines.map((ln) =>
                                      ln.key === line.key ? { ...ln, area_unit: v } : ln
                                    ),
                                  }))
                                }
                              >
                                <SelectTrigger className="rounded-sm border-neutral-300 h-9 text-xs w-[88px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="sqft">sq ft</SelectItem>
                                  <SelectItem value="sqm">m²</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-xs text-neutral-400 flex items-center min-h-9">—</span>
                            )}
                          </TableCell>
                          <TableCell className="py-2 align-top w-12">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-red-600 h-9 w-9 p-0 shrink-0"
                              data-testid={`edit-lead-remove-line-${idx}`}
                              onClick={() =>
                                setLeadEdit((prev) => ({
                                  ...prev,
                                  lines: prev.lines.filter((ln) => ln.key !== line.key),
                                }))
                              }
                            >
                              <Trash size={16} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
            )}
          </div>
          {leadEdit && (
            <div className="shrink-0 border-t border-neutral-200 bg-background px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:pb-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end shadow-[0_-6px_16px_-8px_rgba(0,0,0,0.12)] sm:shadow-none">
              <Button type="button" variant="outline" className="rounded-sm w-full sm:w-auto" onClick={() => setLeadEdit(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white w-full sm:w-auto"
                data-testid="edit-lead-save"
                disabled={leadEditSaving}
                onClick={saveLeadEdit}
              >
                {leadEditSaving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Lead Dialog */}
      <Dialog open={addLeadOpen} onOpenChange={(open) => { if (!open) { setAddLeadOpen(false); setAddLeadForm(null); } }}>
        <DialogContent
          className="w-full max-w-[calc(100vw-1.5rem)] sm:max-w-5xl rounded-sm max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0"
          data-testid="add-lead-dialog"
        >
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-6 space-y-4 sm:space-y-6">
            <DialogHeader className="space-y-2 text-left pr-10 sm:pr-12">
              <DialogTitle className="leading-snug break-words">Add new lead</DialogTitle>
              <DialogDescription className="break-words text-xs sm:text-sm">
                Create a lead manually on behalf of a buyer.
              </DialogDescription>
            </DialogHeader>
            {addLeadForm && (
              <div className="space-y-4">
                {/* Buyer info */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-500">Buyer phone <span className="text-red-500">*</span></Label>
                    <Input
                      className="rounded-sm border-neutral-300 mt-1"
                      placeholder="+91 XXXXX XXXXX"
                      value={addLeadForm.buyer_phone}
                      onChange={(e) => setAddLeadForm({ ...addLeadForm, buyer_phone: e.target.value })}
                      data-testid="add-lead-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-500">Buyer name</Label>
                    <Input
                      className="rounded-sm border-neutral-300 mt-1"
                      placeholder="Optional"
                      value={addLeadForm.buyer_name}
                      onChange={(e) => setAddLeadForm({ ...addLeadForm, buyer_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-500">Company</Label>
                    <Input
                      className="rounded-sm border-neutral-300 mt-1"
                      placeholder="Optional"
                      value={addLeadForm.buyer_company}
                      onChange={(e) => setAddLeadForm({ ...addLeadForm, buyer_company: e.target.value })}
                    />
                  </div>
                </div>
                {/* Type + City + Date */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-500">Requirement type</Label>
                    <Select
                      value={addLeadForm.requirement_type}
                      onValueChange={(v) => setAddLeadForm({ ...addLeadForm, requirement_type: v })}
                    >
                      <SelectTrigger className="rounded-sm border-neutral-300 mt-1" data-testid="add-lead-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rent">Rent</SelectItem>
                        <SelectItem value="buy">Buy</SelectItem>
                        <SelectItem value="sell">Sell</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="relative z-20">
                    <Label className="text-xs font-mono uppercase text-neutral-500">City <span className="text-red-500">*</span></Label>
                    <CityGoogleAutocomplete
                      value={addLeadForm.city}
                      onChange={(v) => setAddLeadForm({ ...addLeadForm, city: v })}
                      onSelect={(v) => setAddLeadForm({ ...addLeadForm, city: v })}
                      placeholder="Search city…"
                      predictionsPlacement="auto"
                      className={ADD_LEAD_CITY_CLASS}
                      containerTestId="add-lead-city"
                      inputTestId="add-lead-city-search"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-500">Start date <span className="text-red-500">*</span></Label>
                    <Input
                      className="rounded-sm border-neutral-300 mt-1"
                      type="date"
                      value={addLeadForm.start_date}
                      onChange={(e) => setAddLeadForm({ ...addLeadForm, start_date: e.target.value })}
                      data-testid="add-lead-start-date"
                    />
                  </div>
                </div>
                {/* Duration (only for rent) */}
                {addLeadForm.requirement_type === "rent" && (
                  <div>
                    <Label className="text-xs font-mono uppercase text-neutral-500">Duration</Label>
                    <Select
                      value={addLeadForm.durationMode}
                      onValueChange={(v) => setAddLeadForm({ ...addLeadForm, durationMode: v })}
                    >
                      <SelectTrigger className="rounded-sm border-neutral-300 mt-1" data-testid="add-lead-duration">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DURATION_OPTIONS.map((d) => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                        <SelectItem value={CUSTOM_DURATION}>Custom…</SelectItem>
                      </SelectContent>
                    </Select>
                    {addLeadForm.durationMode === CUSTOM_DURATION && (
                      <Input
                        className="rounded-sm border-neutral-300 mt-2"
                        placeholder="e.g. 8 weeks"
                        value={addLeadForm.customDuration}
                        onChange={(e) => setAddLeadForm({ ...addLeadForm, customDuration: e.target.value })}
                      />
                    )}
                  </div>
                )}
                {/* Product lines */}
                <div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
                    <Label className="text-xs font-mono uppercase text-neutral-500">Product lines</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-sm text-xs h-7 w-full sm:w-auto shrink-0"
                      data-testid="add-lead-add-line"
                      onClick={() => setAddLeadForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLeadEditLine()] }))}
                    >
                      <Plus size={12} className="mr-0.5" /> Add line
                    </Button>
                  </div>
                  <div className="md:hidden space-y-3">
                    {addLeadForm.lines.map((line, idx) => (
                      <div key={line.key} className="rounded-sm border border-neutral-200 bg-white p-3 space-y-3 shadow-sm">
                        <div className="flex items-center justify-between gap-2 border-b border-neutral-100 pb-2">
                          <span className="text-xs font-mono uppercase text-neutral-500">Line {idx + 1}</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="text-red-600 h-9 w-9 p-0 shrink-0"
                            onClick={() => setAddLeadForm((prev) => ({ ...prev, lines: prev.lines.filter((ln) => ln.key !== line.key) }))}
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] font-mono uppercase text-neutral-500">From catalog</Label>
                          <Select
                            value={line.catalogId}
                            onValueChange={(v) => {
                              setAddLeadForm((prev) => ({
                                ...prev,
                                lines: prev.lines.map((ln) =>
                                  ln.key === line.key ? mapLeadLineCatalogSelection(ln, v, products) : ln
                                ),
                              }));
                            }}
                          >
                            <SelectTrigger className="rounded-sm border-neutral-300 h-9 text-xs w-full">
                              <SelectValue placeholder="Catalog or custom" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={CUSTOM_DURATION}>Custom (type name)</SelectItem>
                              {products.map((pr) => (
                                <SelectItem key={pr.id} value={pr.id}>{pr.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {line.catalogId === CUSTOM_DURATION && (
                          <div className="space-y-1">
                            <Label className="text-[10px] font-mono uppercase text-neutral-500">Custom name</Label>
                            <Input
                              className="rounded-sm border-neutral-300 h-9 text-sm w-full"
                              placeholder="Describe the product"
                              value={line.product_name}
                              onChange={(e) =>
                                setAddLeadForm((prev) => ({
                                  ...prev,
                                  lines: prev.lines.map((ln) => ln.key === line.key ? { ...ln, product_name: e.target.value } : ln),
                                }))
                              }
                            />
                          </div>
                        )}
                        <div className="space-y-1">
                          <Label className="text-[10px] font-mono uppercase text-neutral-500">Size</Label>
                          <LeadLineSizeSelect
                            line={line}
                            products={products}
                            value={line.size}
                            testId={`add-lead-size-m-${idx}`}
                            onChange={(sz) =>
                              setAddLeadForm((prev) => ({
                                ...prev,
                                lines: prev.lines.map((ln) =>
                                  ln.key === line.key ? { ...ln, size: sz } : ln
                                ),
                              }))
                            }
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-[10px] font-mono uppercase text-neutral-500">Spec</Label>
                            <Select
                              value={line.spec_type}
                              onValueChange={(v) =>
                                setAddLeadForm((prev) => ({
                                  ...prev,
                                  lines: prev.lines.map((ln) => ln.key === line.key ? { ...ln, spec_type: v } : ln),
                                }))
                              }
                            >
                              <SelectTrigger className="rounded-sm border-neutral-300 h-9 text-xs w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="quantity">Quantity</SelectItem>
                                <SelectItem value="area">Area</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px] font-mono uppercase text-neutral-500">
                              {line.spec_type === "quantity" ? "Quantity" : "Area"}
                            </Label>
                            {line.spec_type === "quantity" ? (
                              <Input
                                className="rounded-sm border-neutral-300 h-9 w-full text-xs"
                                value={line.quantityStr}
                                onChange={(e) => {
                                  const digits = e.target.value.replace(/\D/g, "");
                                  setAddLeadForm((prev) => ({
                                    ...prev,
                                    lines: prev.lines.map((ln) => ln.key === line.key ? { ...ln, quantityStr: digits } : ln),
                                  }));
                                }}
                              />
                            ) : (
                              <Input
                                className="rounded-sm border-neutral-300 h-9 w-full text-xs"
                                value={line.area_value}
                                onChange={(e) =>
                                  setAddLeadForm((prev) => ({
                                    ...prev,
                                    lines: prev.lines.map((ln) => ln.key === line.key ? { ...ln, area_value: e.target.value } : ln),
                                  }))
                                }
                              />
                            )}
                          </div>
                        </div>
                        {line.spec_type === "area" && (
                          <div className="space-y-1">
                            <Label className="text-[10px] font-mono uppercase text-neutral-500">Unit</Label>
                            <Select
                              value={line.area_unit}
                              onValueChange={(v) =>
                                setAddLeadForm((prev) => ({
                                  ...prev,
                                  lines: prev.lines.map((ln) => ln.key === line.key ? { ...ln, area_unit: v } : ln),
                                }))
                              }
                            >
                              <SelectTrigger className="rounded-sm border-neutral-300 h-9 text-xs w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sqft">sq ft</SelectItem>
                                <SelectItem value="sqm">m²</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="hidden md:block rounded-sm border border-neutral-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-neutral-200 bg-neutral-50">
                            <TableHead className="text-xs font-mono uppercase min-w-[220px]">From catalog</TableHead>
                            <TableHead className="text-xs font-mono uppercase min-w-[220px]">Custom name</TableHead>
                            <TableHead className="text-xs font-mono uppercase w-[120px]">Size</TableHead>
                            <TableHead className="text-xs font-mono uppercase w-[140px]">Spec</TableHead>
                            <TableHead className="text-xs font-mono uppercase min-w-[120px]">Quantity / Area</TableHead>
                            <TableHead className="text-xs font-mono uppercase w-[120px]">Unit</TableHead>
                            <TableHead className="text-xs font-mono uppercase w-[70px] text-right">Remove</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {addLeadForm.lines.map((line, idx) => (
                            <TableRow key={line.key} className="border-neutral-200 align-top">
                              <TableCell className="py-2 align-top">
                                <Select
                                  value={line.catalogId}
                                  onValueChange={(v) => {
                                    setAddLeadForm((prev) => ({
                                      ...prev,
                                      lines: prev.lines.map((ln) =>
                                        ln.key === line.key ? mapLeadLineCatalogSelection(ln, v, products) : ln
                                      ),
                                    }));
                                  }}
                                >
                                  <SelectTrigger className="rounded-sm border-neutral-300 h-9 text-xs max-w-[260px]">
                                    <SelectValue placeholder="Catalog or custom" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={CUSTOM_DURATION}>Custom (type name)</SelectItem>
                                    {products.map((pr) => (
                                      <SelectItem key={pr.id} value={pr.id}>{pr.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="py-2 align-top">
                                {line.catalogId === CUSTOM_DURATION ? (
                                  <Input
                                    className="rounded-sm border-neutral-300 h-9 text-sm"
                                    placeholder="Describe the product"
                                    value={line.product_name}
                                    onChange={(e) =>
                                      setAddLeadForm((prev) => ({
                                        ...prev,
                                        lines: prev.lines.map((ln) =>
                                          ln.key === line.key ? { ...ln, product_name: e.target.value } : ln
                                        ),
                                      }))
                                    }
                                  />
                                ) : (
                                  <span className="text-xs text-neutral-400 flex items-center min-h-9 px-1" title="Name comes from catalog selection">
                                    -
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="py-2 align-top">
                                <LeadLineSizeSelect
                                  line={line}
                                  products={products}
                                  value={line.size}
                                  testId={`add-lead-size-${idx}`}
                                  onChange={(sz) =>
                                    setAddLeadForm((prev) => ({
                                      ...prev,
                                      lines: prev.lines.map((ln) =>
                                        ln.key === line.key ? { ...ln, size: sz } : ln
                                      ),
                                    }))
                                  }
                                />
                              </TableCell>
                              <TableCell className="py-2 align-top">
                                <Select
                                  value={line.spec_type}
                                  onValueChange={(v) =>
                                    setAddLeadForm((prev) => ({
                                      ...prev,
                                      lines: prev.lines.map((ln) =>
                                        ln.key === line.key ? { ...ln, spec_type: v } : ln
                                      ),
                                    }))
                                  }
                                >
                                  <SelectTrigger className="rounded-sm border-neutral-300 h-9 text-xs w-[130px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="quantity">Quantity</SelectItem>
                                    <SelectItem value="area">Area</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="py-2 align-top">
                                {line.spec_type === "quantity" ? (
                                  <Input
                                    className="rounded-sm border-neutral-300 h-9 text-sm max-w-[140px]"
                                    value={line.quantityStr}
                                    onChange={(e) => {
                                      const digits = e.target.value.replace(/\D/g, "");
                                      setAddLeadForm((prev) => ({
                                        ...prev,
                                        lines: prev.lines.map((ln) =>
                                          ln.key === line.key ? { ...ln, quantityStr: digits } : ln
                                        ),
                                      }));
                                    }}
                                  />
                                ) : (
                                  <Input
                                    className="rounded-sm border-neutral-300 h-9 text-sm max-w-[140px]"
                                    value={line.area_value}
                                    onChange={(e) =>
                                      setAddLeadForm((prev) => ({
                                        ...prev,
                                        lines: prev.lines.map((ln) =>
                                          ln.key === line.key ? { ...ln, area_value: e.target.value } : ln
                                        ),
                                      }))
                                    }
                                  />
                                )}
                              </TableCell>
                              <TableCell className="py-2 align-top">
                                {line.spec_type === "area" ? (
                                  <Select
                                    value={line.area_unit}
                                    onValueChange={(v) =>
                                      setAddLeadForm((prev) => ({
                                        ...prev,
                                        lines: prev.lines.map((ln) =>
                                          ln.key === line.key ? { ...ln, area_unit: v } : ln
                                        ),
                                      }))
                                    }
                                  >
                                    <SelectTrigger className="rounded-sm border-neutral-300 h-9 text-xs w-[110px]">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="sqft">sq ft</SelectItem>
                                      <SelectItem value="sqm">m²</SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <span className="text-xs text-neutral-400 flex items-center min-h-9 px-1">-</span>
                                )}
                              </TableCell>
                              <TableCell className="py-2 align-top text-right">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 h-9 w-9 p-0 ml-auto"
                                  onClick={() =>
                                    setAddLeadForm((prev) => ({
                                      ...prev,
                                      lines: prev.lines.filter((ln) => ln.key !== line.key),
                                    }))
                                  }
                                  aria-label={`Remove line ${idx + 1}`}
                                >
                                  <Trash size={16} />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {addLeadForm && (
            <div className="shrink-0 border-t border-neutral-200 bg-background px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:pb-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end shadow-[0_-6px_16px_-8px_rgba(0,0,0,0.12)] sm:shadow-none">
              <Button type="button" variant="outline" className="rounded-sm w-full sm:w-auto" onClick={() => { setAddLeadOpen(false); setAddLeadForm(null); }}>
                Cancel
              </Button>
              <Button
                type="button"
                className="rounded-sm bg-neutral-900 hover:bg-neutral-700 text-white w-full sm:w-auto"
                data-testid="add-lead-save"
                disabled={addLeadSaving}
                onClick={saveAddLead}
              >
                {addLeadSaving ? "Creating…" : "Create lead"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Lead Dialog */}
      <Dialog
        open={!!assignDialog}
        onOpenChange={(open) => {
          if (!open) {
            setAssignDialog(null);
            setSelectedSuppliers([]);
            setAssignSupplierSearch("");
            setAssignSupplierSearchQ("");
            setAssignSearchItems([]);
            setAssignSearchTotal(0);
            setAssignSupplierPage(1);
          }
        }}
      >
        <DialogContent
          className="max-w-lg rounded-sm !flex !flex-col h-[min(85vh,720px)] max-h-[85vh] overflow-hidden p-0 gap-0"
          data-testid="assign-dialog"
        >
          <div className="shrink-0 px-4 sm:px-6 pt-4 sm:pt-6 pr-12 space-y-3">
            <DialogHeader>
              <DialogTitle>Assign Lead to Suppliers</DialogTitle>
            </DialogHeader>
            {assignDialog && (
              <>
                <div className="p-3 bg-neutral-50 rounded-sm border border-neutral-200 text-sm space-y-2">
                  <p><strong>City:</strong> {assignDialog.city}</p>
                  <div>
                    <p className="font-bold">Line quantities</p>
                    <ul className="mt-1 list-disc list-inside text-neutral-700 max-h-24 overflow-y-auto">
                      {assignDialog.items?.map((item, i) => (
                        <li key={i}>
                          {item.product_name}
                          {item.size ? ` (${item.size})` : ""}
                          {item.spec_type === "area" && item.area_value != null
                            ? ` — ${item.area_value} ${item.area_unit === "sqm" ? "m²" : "sq ft"}`
                            : ` — ×${item.quantity ?? "—"}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-neutral-600 mb-2">
                    Select suppliers to assign ({selectedSuppliers.length} selected)
                  </p>
                  <div className="relative">
                    <MagnifyingGlass
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
                      aria-hidden
                    />
                    <Input
                      data-testid="assign-supplier-search"
                      className="rounded-sm border-neutral-300 pl-9 pr-9"
                      placeholder="Search by name or city…"
                      value={assignSupplierSearch}
                      onChange={(e) => {
                        setAssignSupplierSearch(e.target.value);
                        setAssignSupplierPage(1);
                      }}
                      aria-label="Search suppliers by name or city"
                    />
                    {assignSupplierSearch ? (
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-sm text-neutral-500 hover:bg-neutral-100"
                        onClick={() => {
                          setAssignSupplierSearch("");
                          setAssignSupplierPage(1);
                        }}
                        aria-label="Clear supplier search"
                      >
                        <X size={16} />
                      </button>
                    ) : null}
                  </div>
                </div>
              </>
            )}
          </div>

          {assignDialog && (
            <>
              <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-3 space-y-4">
                {assignDialogAssignments.length > 0 && (
                  <div
                    className="p-3 rounded-sm border border-amber-200 bg-amber-50/70 text-sm"
                    data-testid="assign-already-assigned-section"
                  >
                    <p className="text-xs font-mono uppercase text-amber-900 mb-2 font-bold">Already assigned</p>
                    <ul className="space-y-2">
                      {assignDialogAssignments.map((a) => {
                        const pipe = ASSIGNMENT_PIPELINE[a.status] || {
                          label: a.status ? String(a.status).replace(/_/g, " ") : "—",
                          color: "bg-neutral-100 text-neutral-800 border-neutral-300",
                        };
                        return (
                          <li key={a.id} className="flex items-center justify-between gap-2 flex-wrap">
                            <span className="font-medium text-neutral-900">
                              {a.supplier_name || a.supplier_id || "Supplier"}
                            </span>
                            <Badge className={`rounded-sm text-xs border capitalize ${pipe.color}`}>{pipe.label}</Badge>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="text-[11px] text-neutral-600 mt-2">
                      These suppliers are included in your selection below. Confirming updates assignments for this lead.
                    </p>
                  </div>
                )}

                <div data-testid="assign-supplier-list">
                  <p className="text-xs font-mono uppercase text-neutral-500 mb-2 font-bold">
                    Suppliers
                    {assignIsApiSearch
                      ? assignSearchBusy
                        ? " (searching…)"
                        : ` (${assignSupplierListTotal} match${assignSupplierListTotal === 1 ? "" : "es"})`
                      : ` (${assignSupplierRows.length})`}
                  </p>

                  {assignSearchBusy ? (
                    <p className="text-sm text-neutral-500 py-6 text-center" data-testid="assign-supplier-search-loading">
                      Searching suppliers…
                    </p>
                  ) : assignSupplierRows.length === 0 ? (
                    <p className="text-sm text-neutral-500 py-6 text-center" data-testid="assign-supplier-search-empty">
                      {assignIsApiSearch
                        ? `No suppliers match “${assignSearchTyped}”.`
                        : "No suppliers available to assign."}
                    </p>
                  ) : (
                    assignSupplierPageRows.map((s) => {
                      const groupLabel =
                        s.matchGroup === "city"
                          ? `City match · ${assignDialog.city}`
                          : s.matchGroup === "product"
                            ? "Product match"
                            : "Other";
                      const groupClass =
                        s.matchGroup === "city" || s.matchGroup === "product"
                          ? "text-green-700"
                          : "text-neutral-500";
                      return (
                        <label
                          key={`assign-row-${s.matchGroup}-${s.id}`}
                          className="flex items-center gap-3 p-2 hover:bg-neutral-50 rounded-sm cursor-pointer"
                          data-testid={`assign-supplier-${s.matchGroup}-${s.id}`}
                        >
                          <Checkbox
                            checked={selectedSuppliers.includes(s.id)}
                            onCheckedChange={() => toggleSupplier(s.id)}
                            className="rounded-sm"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{s.name || s.company_name || "Supplier"}</p>
                            <p className={`text-[11px] font-mono uppercase ${groupClass}`}>{groupLabel}</p>
                            <p className="text-xs text-neutral-500">
                              {s.supplier_type} &middot; Score: {s.performance_score}% &middot; Leads: {s.total_leads ?? 0}
                              {supplierCitiesSearchText(s) ? (
                                <> &middot; {Array.isArray(s.cities_served) ? s.cities_served.join(", ") : String(s.cities_served)}</>
                              ) : null}
                            </p>
                            {(s.matchGroup === "city" || s.matchGroup === "product") && (
                              <AssignSupplierQtyHint
                                supplier={s}
                                assignDialog={assignDialog}
                                resolveItemProductId={resolveItemProductId}
                              />
                            )}
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>

                {filteredAssignOrphanAssignments.length > 0 && (
                  <div data-testid="assign-orphan-suppliers">
                    <p className="text-xs font-mono uppercase text-neutral-600 mb-2 font-bold">
                      Already assigned (not in lists above)
                    </p>
                    {filteredAssignOrphanAssignments.map((a) => {
                      const pipe = ASSIGNMENT_PIPELINE[a.status] || {
                        label: a.status ? String(a.status).replace(/_/g, " ") : "—",
                        color: "bg-neutral-100 text-neutral-800 border-neutral-300",
                      };
                      return (
                        <label
                          key={a.id}
                          className="flex items-center gap-3 p-2 hover:bg-neutral-50 rounded-sm cursor-pointer"
                          data-testid={`assign-supplier-orphan-${a.id}`}
                        >
                          <Checkbox
                            checked={selectedSuppliers.includes(a.supplier_id)}
                            onCheckedChange={() => toggleSupplier(a.supplier_id)}
                            className="rounded-sm"
                          />
                          <div className="flex-1 min-w-0 flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-medium">{a.supplier_name || a.supplier_id}</p>
                            <Badge className={`rounded-sm text-xs border capitalize shrink-0 ${pipe.color}`}>{pipe.label}</Badge>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="shrink-0 border-t border-neutral-200 bg-background px-4 sm:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] space-y-3">
                {!assignSearchBusy && assignSupplierListTotal > 0 && (
                  <div
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2"
                    data-testid="assign-supplier-pagination"
                  >
                    <p className="text-xs text-neutral-600 tabular-nums">
                      {(() => {
                        const total = assignSupplierListTotal;
                        const from = (assignSupplierPageSafe - 1) * assignListPageSize + 1;
                        const to = Math.min(total, assignSupplierPageSafe * assignListPageSize);
                        return `Showing ${from}-${to} of ${total}`;
                      })()}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-neutral-600 tabular-nums">
                        Page {assignSupplierPageSafe} / {assignSupplierPageCount}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={assignSupplierPageSafe <= 1}
                        onClick={() => setAssignSupplierPage((p) => Math.max(1, p - 1))}
                        className="rounded-sm h-8 px-2 text-xs border-neutral-300"
                        data-testid="assign-supplier-prev"
                      >
                        Prev
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={assignSupplierPageSafe >= assignSupplierPageCount}
                        onClick={() => setAssignSupplierPage((p) => Math.min(assignSupplierPageCount, p + 1))}
                        className="rounded-sm h-8 px-2 text-xs border-neutral-300"
                        data-testid="assign-supplier-next"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
                <Button
                  data-testid="confirm-assign-btn"
                  onClick={assignLead}
                  disabled={
                    selectedSuppliers.length < minRequired ||
                    selectedSuppliers.length > 7 ||
                    !(assignDialog?.items?.length > 0)
                  }
                  title={!(assignDialog?.items?.length > 0) ? "This lead has no product lines" : undefined}
                  className="w-full rounded-sm bg-orange-600 hover:bg-orange-700 text-white font-bold disabled:opacity-50 disabled:pointer-events-auto"
                >
                  Assign to {selectedSuppliers.length} Suppliers
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Supplier pipeline status for a lead */}
      <Dialog open={!!pipelineStatusDialog} onOpenChange={(open) => !open && setPipelineStatusDialog(null)}>
        <DialogContent className="max-w-lg rounded-sm max-h-[80vh] overflow-y-auto" data-testid="pipeline-status-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pulse size={22} weight="bold" className="text-orange-600" />
              Supplier pipeline status
            </DialogTitle>
            <DialogDescription>
              Change each supplier&apos;s pipeline stage as needed. Stages can be updated at any time.
            </DialogDescription>
          </DialogHeader>
          {pipelineStatusDialog && (
            <div className="space-y-4">
              <div className="p-3 bg-neutral-50 rounded-sm border border-neutral-200 text-sm space-y-1">
                <p><strong>Lead ID:</strong> <span className="font-mono text-xs">{pipelineStatusDialog.lead.id.slice(0, 8)}…</span></p>
                <p><strong>City:</strong> {pipelineStatusDialog.lead.city}</p>
                <p><strong>Phone:</strong> {pipelineStatusDialog.lead.buyer_phone}</p>
              </div>
              {pipelineStatusLoading ? (
                <p className="text-xs text-neutral-500">Updating from server…</p>
              ) : null}
              <div className="border border-neutral-200 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-neutral-200 bg-neutral-50">
                      <TableHead className="text-xs font-mono uppercase w-12">#</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Supplier</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Pipeline stage</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Assigned</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pipelineStatusDialog.assignments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-6 text-neutral-400 text-sm">
                          No suppliers assigned yet. Use <strong>Assign</strong> beside this lead to send it to suppliers.
                        </TableCell>
                      </TableRow>
                    ) : (
                      pipelineStatusDialog.assignments.map((a, i) => {
                        const stageOptions = assignmentStageOptions(a);
                        return (
                          <TableRow key={a.id} className="border-neutral-100" data-testid={`pipeline-row-${a.id}`}>
                            <TableCell className="text-xs font-mono text-neutral-500">{i + 1}</TableCell>
                            <TableCell className="text-sm font-medium">{a.supplier_name || a.supplier_id || "—"}</TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1 items-start">
                                <Select
                                  value={a.status}
                                  onValueChange={(v) => updateAssignmentStage(a.id, v)}
                                  disabled={pipelineStageSavingId === a.id}
                                >
                                  <SelectTrigger
                                    data-testid={`admin-pipeline-stage-${a.id}`}
                                    className="h-8 w-full min-w-[148px] rounded-sm border-neutral-300 text-xs"
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {stageOptions.map((stage) => (
                                      <SelectItem key={stage.key} value={stage.key}>
                                        {stage.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {a.is_first_responder ? (
                                  <span className="text-[10px] font-bold text-violet-700">First quotation sent</span>
                                ) : null}
                                {a.is_expired ? (
                                  <span className="text-[10px] text-neutral-500">Assignment window expired</span>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-neutral-600 font-mono align-top">
                              {a.assigned_at
                                ? new Date(a.assigned_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
                                : "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-sm border-neutral-300"
                disabled={pipelineStatusLoading}
                data-testid="pipeline-status-refresh-btn"
                onClick={async () => {
                  const lead = pipelineStatusDialog.lead;
                  if (!lead?.id) return;
                  setPipelineStatusLoading(true);
                  try {
                    const r = await api.get(`/leads/${lead.id}`);
                    const raw = r.data && r.data.assignments;
                    const list = Array.isArray(raw) ? raw : [];
                    setPipelineStatusDialog((prev) => (prev ? { ...prev, assignments: list } : null));
                    fetchAll();
                  } catch {
                    toast.error("Could not refresh status");
                  } finally {
                    setPipelineStatusLoading(false);
                  }
                }}
              >
                Refresh status
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Only these rows appear in “Under parent” for subcategories */}
      <Dialog open={parentCategoriesModalOpen} onOpenChange={setParentCategoriesModalOpen}>
        <DialogContent className="max-w-lg rounded-sm max-h-[85vh] overflow-y-auto" data-testid="parent-categories-modal">
          <DialogHeader>
            <DialogTitle>Parent categories</DialogTitle>
            <DialogDescription>
              These groups are the only options in the subcategory “Under parent” list. Add new groups here — not from the full category table.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {designatedParentCategories.length === 0 ? (
              <p className="text-sm text-neutral-500 py-6 text-center border border-dashed border-neutral-200 rounded-sm">
                No parent groups yet. Use “Add parent category” below (e.g. Scaffolding, Shuttering).
              </p>
            ) : (
              <div className="rounded-sm border border-neutral-200 max-h-[min(320px,45vh)] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-neutral-200 bg-neutral-50">
                      <TableHead className="text-xs font-mono uppercase w-12">#</TableHead>
                      <TableHead className="text-xs font-mono uppercase">Name</TableHead>
                      <TableHead className="text-xs font-mono uppercase w-[88px]"> </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {designatedParentCategories.map((c, i) => (
                      <TableRow key={c.id} className="border-neutral-100">
                        <TableCell className="text-xs font-mono text-neutral-500 py-2">{i + 1}</TableCell>
                        <TableCell className="font-medium text-sm py-2">{c.name}</TableCell>
                        <TableCell className="py-2 text-right">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-sm h-7 text-xs border-neutral-300"
                            onClick={() => {
                              setParentCategoriesModalOpen(false);
                              openCategoryEdit(c);
                            }}
                            data-testid={`parent-modal-edit-${c.id}`}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <Button
              type="button"
              className="w-full rounded-sm bg-orange-600 hover:bg-orange-700 text-white"
              onClick={() => {
                setParentCategoriesModalOpen(false);
                openCategoryCreateParent();
              }}
              data-testid="parent-modal-add-btn"
            >
              <Plus size={14} className="mr-1 inline" /> Add parent category
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Create/Edit Dialog */}
      <Dialog open={!!categoryDialogMode} onOpenChange={(open) => !open && setCategoryDialogMode(null)}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>
              {categoryDialogMode === "create-parent"
                ? "Add parent category"
                : categoryDialogMode === "create-sub"
                  ? "Add subcategory"
                  : "Edit category"}
            </DialogTitle>
            <DialogDescription>
              {categoryDialogMode === "create-parent"
                ? "Creates a top-level group (e.g. Scaffolding, Shuttering). Slug is generated from the name. Then use “Add subcategory” for items under this group."
                : categoryDialogMode === "create-sub"
                  ? "Nest under a parent group only. Subcategories appear indented; product lists use leaf categories."
                  : "Slug is auto-generated from the name when applicable. Configure type/tier, order, and active status."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs font-mono uppercase text-neutral-500">Name</Label>
                <Input
                  className="rounded-sm border-neutral-300 mt-1"
                  value={categoryForm.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setCategoryForm((prev) => ({
                      ...prev,
                      name,
                    }));
                  }}
                  placeholder={
                    categoryDialogMode === "create-parent"
                      ? "e.g. Scaffolding, Shuttering & Formwork"
                      : "Subcategory name"
                  }
                  data-testid="category-name"
                />
              </div>
              {categoryDialogShowsParentField ? (
                <div className="sm:col-span-2">
                  <Label className="text-xs font-mono uppercase text-neutral-500">Under parent</Label>
                  <Select
                    value={
                      categoryDialogMode === "create-sub"
                        ? categoryForm.parent_id || SUB_PARENT_UNSET
                        : categoryForm.parent_id
                          ? categoryForm.parent_id
                          : CATEGORY_PARENT_NONE
                    }
                    onValueChange={(v) => {
                      if (categoryDialogMode === "create-sub" && v === SUB_PARENT_UNSET) return;
                      setCategoryForm((p) => ({
                        ...p,
                        parent_id:
                          v === CATEGORY_PARENT_NONE || v === SUB_PARENT_UNSET ? "" : v,
                      }));
                    }}
                  >
                    <SelectTrigger className="rounded-sm border-neutral-300 mt-1" data-testid="category-parent">
                      <SelectValue placeholder="Select parent group" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryDialogMode === "create-sub" ? (
                        <SelectItem value={SUB_PARENT_UNSET} disabled className="text-neutral-400">
                          Select a parent group…
                        </SelectItem>
                      ) : (
                        <SelectItem value={CATEGORY_PARENT_NONE}>Top-level (not under a group)</SelectItem>
                      )}
                      {designatedParentCategories.map((opt) => (
                        <SelectItem key={opt.id} value={opt.id}>
                          {opt.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-neutral-500 mt-1">
                    Only categories listed under Parent categories appear here (not Cement, Clamps, etc. unless marked as parent groups).
                  </p>
                </div>
              ) : null}
              {categoryDialogShowsParentGroupCheckbox ? (
                <div className="sm:col-span-2">
                  <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                    <Checkbox
                      checked={categoryForm.is_parent_group === true}
                      onCheckedChange={(c) => setCategoryForm((p) => ({ ...p, is_parent_group: c === true }))}
                      className="rounded-sm mt-0.5"
                      data-testid="category-is-parent-group"
                    />
                    <span>
                      <span className="font-medium text-neutral-800">Parent group</span>
                      <span className="block text-xs text-neutral-500 mt-0.5">
                        Allow subcategories under this row. It will then appear in “Under parent” when adding subcategories.
                      </span>
                    </span>
                  </label>
                </div>
              ) : null}
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Type</Label>
                <Select value={categoryForm.type} onValueChange={(v) => setCategoryForm((p) => ({ ...p, type: v }))}>
                  <SelectTrigger className="rounded-sm border-neutral-300 mt-1" data-testid="category-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rent">Rent</SelectItem>
                    <SelectItem value="buy">Buy</SelectItem>
                    <SelectItem value="sell">Sell</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Tier</Label>
                <Select value={categoryForm.tier} onValueChange={(v) => setCategoryForm((p) => ({ ...p, tier: v }))}>
                  <SelectTrigger className="rounded-sm border-neutral-300 mt-1" data-testid="category-tier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="listed">Listed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Sort order</Label>
                <Input
                  className="rounded-sm border-neutral-300 mt-1 font-mono"
                  inputMode="numeric"
                  value={String(categoryForm.sort_order ?? 0)}
                  onChange={(e) =>
                    setCategoryForm((p) => ({
                      ...p,
                      sort_order: Number(String(e.target.value).replace(/\D/g, "")) || 0,
                    }))
                  }
                  data-testid="category-sort-order"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <Checkbox
                    checked={categoryForm.is_active === true}
                    onCheckedChange={(c) => setCategoryForm((p) => ({ ...p, is_active: c === true }))}
                    className="rounded-sm"
                    data-testid="category-active"
                  />
                  <span>Active</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" className="rounded-sm" onClick={() => setCategoryDialogMode(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white"
                disabled={categorySaving}
                onClick={saveCategory}
                data-testid="category-save"
              >
                {categorySaving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog
        open={productDialog}
        onOpenChange={(open) => {
          setProductDialog(open);
          if (!open) setNewProductImageFile(null);
        }}
      >
        <DialogContent className="max-w-sm rounded-sm" data-testid="add-product-dialog">
          <DialogHeader><DialogTitle>Add Product</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="product-name-input" placeholder="Product name" value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} className="rounded-sm border-neutral-300" />
            <Select value={productSubcategoryOptions.includes(newProduct.category) ? newProduct.category : productSubcategoryOptions[0]} onValueChange={v => setNewProduct({ ...newProduct, category: v })}>
              <SelectTrigger data-testid="product-category-select" className="rounded-sm border-neutral-300"><SelectValue placeholder="Subcategory" /></SelectTrigger>
              <SelectContent>
                {productSubcategoryOptions.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">Product image (optional)</Label>
              <Input
                data-testid="product-image-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="rounded-sm border-neutral-300 mt-1 cursor-pointer"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  if (f.size > PRODUCT_IMAGE_MAX_BYTES) {
                    toast.error(`Image must be at most ${PRODUCT_IMAGE_MAX_KB} KB`);
                    return;
                  }
                  setNewProductImageFile(f);
                  setNewProduct((prev) => ({ ...prev, image_url: "" }));
                }}
              />
              <p className="text-[11px] text-neutral-500 mt-1">JPEG, PNG, WebP, or GIF — max {PRODUCT_IMAGE_MAX_KB} KB. Upload is stored on the server.</p>
              {newProductImageFile && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="w-14 h-14 rounded-sm overflow-hidden bg-neutral-100 border border-neutral-200 shrink-0">
                    <img src={newProductImagePreview} alt="" className="w-full h-full object-cover" />
                  </div>
                  <Button type="button" variant="outline" size="sm" className="rounded-sm text-xs h-7" onClick={() => setNewProductImageFile(null)}>
                    Remove file
                  </Button>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">Or image URL</Label>
              <Input
                data-testid="product-image-input"
                placeholder="https://… (optional)"
                value={newProduct.image_url}
                disabled={!!newProductImageFile}
                onChange={e => setNewProduct({ ...newProduct, image_url: e.target.value })}
                className="rounded-sm border-neutral-300 mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">Sizes (optional)</Label>
              <div className="mt-1">
                <ProductSizesEditor
                  sizes={newProduct.sizeList}
                  onChange={(sizeList) => setNewProduct({ ...newProduct, sizeList })}
                  testIdPrefix="product-sizes"
                />
              </div>
            </div>
            <Button data-testid="save-product-btn" onClick={addProduct} className="w-full rounded-sm bg-orange-600 hover:bg-orange-700 text-white font-bold">Add Product</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog
        open={!!productEdit}
        onOpenChange={(open) => {
          if (!open) {
            setProductEdit(null);
            setProductEditImageFile(null);
          }
        }}
      >
        <DialogContent className="max-w-sm rounded-sm" data-testid="edit-product-dialog">
          <DialogHeader>
            <DialogTitle>Edit product</DialogTitle>
            <DialogDescription>Assign a subcategory, optional image, and available sizes for buyers.</DialogDescription>
          </DialogHeader>
          {productEdit && (
            <div className="space-y-3">
              <Input
                data-testid="edit-product-name-input"
                placeholder="Product name"
                value={productEdit.name}
                onChange={(e) => setProductEdit({ ...productEdit, name: e.target.value })}
                className="rounded-sm border-neutral-300"
              />
              <Select
                value={
                  editProductCategoryChoices.includes(productEdit.category)
                    ? productEdit.category
                    : editProductCategoryChoices[0]
                }
                onValueChange={(v) => setProductEdit({ ...productEdit, category: v })}
              >
                <SelectTrigger data-testid="edit-product-category-select" className="rounded-sm border-neutral-300">
                  <SelectValue placeholder="Subcategory" />
                </SelectTrigger>
                <SelectContent>
                  {editProductCategoryChoices.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Replace image (optional)</Label>
                <Input
                  data-testid="edit-product-image-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="rounded-sm border-neutral-300 mt-1 cursor-pointer"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (!f) return;
                    if (f.size > PRODUCT_IMAGE_MAX_BYTES) {
                      toast.error(`Image must be at most ${PRODUCT_IMAGE_MAX_KB} KB`);
                      return;
                    }
                    setProductEditImageFile(f);
                    setProductEdit((prev) => (prev ? { ...prev, image_url: "" } : prev));
                  }}
                />
                {productEditImageFile && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="w-14 h-14 rounded-sm overflow-hidden bg-neutral-100 border border-neutral-200 shrink-0">
                      <img src={productEditImagePreview} alt="" className="w-full h-full object-cover" />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-sm text-xs h-7"
                      onClick={() => setProductEditImageFile(null)}
                    >
                      Remove file
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Or image URL</Label>
                <Input
                  data-testid="edit-product-image-url-input"
                  placeholder="https://… (optional)"
                  value={productEdit.image_url}
                  disabled={!!productEditImageFile}
                  onChange={(e) => setProductEdit({ ...productEdit, image_url: e.target.value })}
                  className="rounded-sm border-neutral-300 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Sizes (optional)</Label>
                <div className="mt-1">
                  <ProductSizesEditor
                    sizes={productEdit.sizeList || []}
                    onChange={(sizeList) => setProductEdit({ ...productEdit, sizeList })}
                    testIdPrefix="edit-product-sizes"
                  />
                </div>
              </div>
              <Button
                data-testid="save-edit-product-btn"
                onClick={saveEditProduct}
                className="w-full rounded-sm bg-orange-600 hover:bg-orange-700 text-white font-bold"
              >
                Save changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Service Provider Dialog */}
      <Dialog open={spDialog} onOpenChange={setSpDialog}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="add-sp-dialog">
          <DialogHeader><DialogTitle>Add Service Provider</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="sp-name-input" placeholder="Provider name" value={newSP.name} onChange={e => setNewSP({ ...newSP, name: e.target.value })} className="rounded-sm border-neutral-300" />
            <Select value={newSP.category} onValueChange={v => setNewSP({ ...newSP, category: v })}>
              <SelectTrigger data-testid="sp-category-select" className="rounded-sm border-neutral-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="labor">Labor</SelectItem>
                <SelectItem value="transport">Transport</SelectItem>
              </SelectContent>
            </Select>
            <Input data-testid="sp-phone-input" placeholder="Phone" value={newSP.phone} onChange={e => setNewSP({ ...newSP, phone: e.target.value })} className="rounded-sm border-neutral-300" />
            <Input data-testid="sp-city-input" placeholder="City" value={newSP.city} onChange={e => setNewSP({ ...newSP, city: e.target.value })} className="rounded-sm border-neutral-300" />
            <Button data-testid="save-sp-btn" onClick={addSP} className="w-full rounded-sm bg-orange-600 hover:bg-orange-700 text-white font-bold">Add Provider</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectDialog} onOpenChange={(open) => !open && setRejectDialog(null)}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="reject-pr-dialog">
          <DialogHeader>
            <DialogTitle>Reject catalog request</DialogTitle>
            <DialogDescription>Optional note shown to the supplier.</DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="reject-pr-note"
            className="rounded-sm border-neutral-300 min-h-[80px]"
            placeholder="Reason (optional)"
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
          />
          <Button
            data-testid="reject-pr-confirm"
            className="w-full rounded-sm bg-red-600 hover:bg-red-700 text-white"
            onClick={confirmRejectProductRequest}
          >
            Confirm reject
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!supplierEdit}
        onOpenChange={(open) => {
          if (!open) {
            setSupplierEdit(null);
            setSupplierEditCitySearch("");
          }
        }}
      >
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="supplier-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit supplier</DialogTitle>
            <DialogDescription>Update profile, service area, catalog capacity, and optionally reset the supplier login password.</DialogDescription>
          </DialogHeader>
          {supplierEdit && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-500">Name</Label>
                  <Input className="rounded-sm border-neutral-300 mt-1" data-testid="supplier-edit-name" value={supplierEdit.name} onChange={(e) => setSupplierEdit({ ...supplierEdit, name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-500">Email</Label>
                  <Input type="email" className="rounded-sm border-neutral-300 mt-1" data-testid="supplier-edit-email" value={supplierEdit.email} onChange={(e) => setSupplierEdit({ ...supplierEdit, email: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-500">Phone</Label>
                  <Input className="rounded-sm border-neutral-300 mt-1" data-testid="supplier-edit-phone" value={supplierEdit.phone} onChange={(e) => setSupplierEdit({ ...supplierEdit, phone: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-500">Company</Label>
                  <Input className="rounded-sm border-neutral-300 mt-1" data-testid="supplier-edit-company" value={supplierEdit.company_name} onChange={(e) => setSupplierEdit({ ...supplierEdit, company_name: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-mono uppercase text-neutral-500">New password (leave blank to keep)</Label>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    className="rounded-sm border-neutral-300 mt-1"
                    data-testid="supplier-edit-password"
                    value={supplierEdit.password}
                    onChange={(e) => setSupplierEdit({ ...supplierEdit, password: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Type</Label>
                <Select value={supplierEdit.supplier_type} onValueChange={(v) => setSupplierEdit({ ...supplierEdit, supplier_type: v })}>
                  <SelectTrigger className="rounded-sm border-neutral-300 mt-1" data-testid="supplier-edit-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rental">Rental</SelectItem>
                    <SelectItem value="sale">Sale</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={supplierEdit.is_active}
                  onCheckedChange={(c) => setSupplierEdit({ ...supplierEdit, is_active: c === true })}
                  className="rounded-sm"
                  data-testid="supplier-edit-active"
                />
                <span>Active (shown on public supplier directory)</span>
              </label>
              <div className="relative z-20">
                <Label className="text-xs font-mono uppercase text-neutral-500">Cities served</Label>
                <p className="mt-0.5 text-xs text-neutral-500">
                  Search with Google Places and add every city where this supplier operates.
                </p>
                <div className="mt-2">
                  <CityGoogleAutocomplete
                    value={supplierEditCitySearch}
                    onChange={setSupplierEditCitySearch}
                    onSelect={addSupplierEditCityFromSearch}
                    placeholder="Search cities to add…"
                    predictionsPlacement="auto"
                    className={SUPPLIER_EDIT_CITY_CLASS}
                    containerTestId="supplier-edit-city-autocomplete"
                    inputTestId="supplier-edit-city-search"
                  />
                </div>
                {supplierEdit.cities.length > 0 ? (
                  <div className="mt-2.5 flex flex-wrap gap-2 max-h-28 overflow-y-auto">
                    {supplierEdit.cities.map((city) => (
                      <button
                        key={`supplier-edit-city-${city}`}
                        type="button"
                        data-testid={`supplier-edit-city-chip-${city}`}
                        onClick={() => removeSupplierEditCity(city)}
                        className="group inline-flex max-w-[11rem] min-w-0 items-center gap-1.5 rounded-sm border border-orange-200 bg-orange-50 px-2.5 py-1.5 text-left text-xs font-medium text-orange-900 transition-colors hover:border-orange-300 hover:bg-orange-100"
                        title={city}
                      >
                        <MapPin size={12} className="shrink-0 text-orange-500" weight="fill" />
                        <span className="min-w-0 flex-1 truncate">{city}</span>
                        <X
                          size={12}
                          className="shrink-0 text-orange-400 transition-colors group-hover:text-orange-700"
                        />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="mt-2.5 rounded-sm border border-dashed border-neutral-200 bg-neutral-50/60 px-4 py-4 text-center">
                    <p className="text-xs text-neutral-500">No cities added yet — search above to add one</p>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Products &amp; quantities</Label>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 border border-neutral-200 rounded-sm mt-1">
                  {products.map((p) => {
                    const selected = supplierEdit.productLines.some((l) => l.product_id === p.id);
                    return (
                      <Badge
                        key={p.id}
                        variant={selected ? "default" : "outline"}
                        className={`cursor-pointer rounded-sm text-xs max-w-full truncate ${selected ? "bg-orange-600 hover:bg-orange-700 text-white" : "hover:bg-neutral-100"}`}
                        title={p.name}
                        onClick={() => toggleSupplierEditProduct(p.id)}
                      >
                        {p.name}
                      </Badge>
                    );
                  })}
                </div>
                {supplierEdit.productLines.length > 0 && (
                  <div className="mt-2 space-y-2 border border-neutral-200 rounded-sm p-2 bg-neutral-50">
                    <p className="text-xs font-mono uppercase text-neutral-500">Quantity per product</p>
                    {supplierEdit.productLines.map((l) => {
                      const p = products.find((x) => x.id === l.product_id);
                      return (
                        <div key={l.product_id} className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs flex-1 min-w-0 truncate font-medium">{p?.name || l.product_id}</span>
                          <Input
                            type="text"
                            inputMode="numeric"
                            className="w-24 h-8 rounded-sm border-neutral-300 text-xs"
                            data-testid={`supplier-edit-qty-${l.product_id}`}
                            value={l.quantityStr}
                            onChange={(e) => setSupplierEditLineQty(l.product_id, e.target.value)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => setSupplierEdit(null)}>Cancel</Button>
                <Button type="button" className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white" data-testid="supplier-edit-save" disabled={supplierEditSaving} onClick={saveSupplierEdit}>
                  {supplierEditSaving ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!staffDialogMode} onOpenChange={(open) => !open && setStaffDialogMode(null)}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="staff-user-dialog">
          <DialogHeader>
            <DialogTitle>{staffDialogMode === "create" ? "Add staff user" : "Edit staff user"}</DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">
              Staff sign in at <span className="font-mono text-neutral-700">/staff/login</span> and use their own dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">Email</Label>
              <Input
                className="rounded-sm mt-1 border-neutral-300"
                type="email"
                autoComplete="email"
                value={staffForm.email}
                onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">Display name</Label>
              <Input
                className="rounded-sm mt-1 border-neutral-300"
                value={staffForm.name}
                onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">Phone</Label>
              <Input
                className="rounded-sm mt-1 border-neutral-300"
                type="tel"
                autoComplete="tel"
                placeholder="Optional"
                value={staffForm.phone}
                onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">
                {staffDialogMode === "create" ? "Password" : "New password (leave blank to keep)"}
              </Label>
              <Input
                className="rounded-sm mt-1 border-neutral-300"
                type="password"
                autoComplete={staffDialogMode === "create" ? "new-password" : "new-password"}
                value={staffForm.password}
                onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })}
              />
            </div>
            {staffDialogMode === "edit" && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="staff-active"
                  checked={staffForm.is_active}
                  onCheckedChange={(c) => setStaffForm({ ...staffForm, is_active: c === true })}
                />
                <Label htmlFor="staff-active" className="text-sm cursor-pointer">Active</Label>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" className="rounded-sm" onClick={() => setStaffDialogMode(null)}>Cancel</Button>
              <Button
                type="button"
                className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white"
                disabled={staffSaving}
                onClick={saveStaffUser}
              >
                {staffSaving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!staffDeleteTarget} onOpenChange={(open) => !open && setStaffDeleteTarget(null)}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="staff-delete-dialog">
          <DialogHeader>
            <DialogTitle>Deactivate staff user?</DialogTitle>
            <DialogDescription>
              <strong>{staffDeleteTarget?.email}</strong> will lose access. Assigned leads are redistributed to other active staff, or left unassigned if none remain.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setStaffDeleteTarget(null)}>Cancel</Button>
            <Button type="button" className="rounded-sm bg-red-600 hover:bg-red-700 text-white" onClick={confirmDeleteStaff}>Deactivate</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!supplierDeleteTarget} onOpenChange={(open) => !open && setSupplierDeleteTarget(null)}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="supplier-delete-dialog">
          <DialogHeader>
            <DialogTitle>Deactivate supplier?</DialogTitle>
            <DialogDescription>
              <strong>{supplierDeleteTarget?.name}</strong> will be hidden from the public supplier directory. Their account and history are kept; you can turn them active again from Edit.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setSupplierDeleteTarget(null)}>Cancel</Button>
            <Button
              type="button"
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              data-testid="supplier-delete-confirm"
              disabled={supplierDeleteLoading}
              onClick={confirmDeleteSupplier}
            >
              {supplierDeleteLoading ? "Working…" : "Deactivate"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!leadDeleteTarget} onOpenChange={(open) => !open && setLeadDeleteTarget(null)}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="lead-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete lead?</DialogTitle>
            <DialogDescription>
              This permanently removes lead <strong>{leadDeleteTarget?.label}</strong> and all of its supplier assignments.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setLeadDeleteTarget(null)}>Cancel</Button>
            <Button
              type="button"
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              data-testid="lead-delete-confirm"
              disabled={leadDeleteLoading}
              onClick={confirmDeleteLead}
            >
              {leadDeleteLoading ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkLeadDeleteOpen} onOpenChange={setBulkLeadDeleteOpen}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="lead-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete selected leads?</DialogTitle>
            <DialogDescription>
              This permanently removes the selected leads and all of their supplier assignments.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-neutral-700">
            Visible selected:{" "}
            <span className="font-mono">{visibleSelectionState.selectedCount}</span>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              className="rounded-sm"
              disabled={bulkLeadDeleteLoading}
              onClick={() => setBulkLeadDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              data-testid="lead-bulk-delete-confirm"
              disabled={bulkLeadDeleteLoading || visibleSelectionState.selectedCount === 0}
              onClick={confirmBulkDeleteLeads}
            >
              {bulkLeadDeleteLoading ? "Deleting…" : "Delete selected"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
